// Ce fichier génère toutes les modals de documents en production
// Chaque modal utilise DocumentInfoModal avec une configuration spécifique

import DocumentInfoModal from './DocumentInfoModal';
import { DOCUMENT_TYPES } from '../data/gestionDocumentaireTypes';
import { 
  Target, 
  BarChart, 
  FileCheck,
  Users as UsersIcon,
  DollarSign,
  ListChecks,
  AlertTriangle,
  AlertCircle,
  Star,
  Building2,
  Rocket,
  Shield,
  Eye,
  Zap,
  MessageSquare,
  Calendar,
  TrendingUp,
  CheckCircle2,
  Layers,
  Clock,
  FileText
} from 'lucide-react';

// Helper pour créer une modal générique
function createDocumentModal(code: string, config: any, mockContext?: string) {
  return function DocumentModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const docType = DOCUMENT_TYPES.find(d => d.code === code)!;
    return (
      <DocumentInfoModal
        isOpen={isOpen}
        onClose={onClose}
        documentType={docType}
        contentConfig={config}
        mockContext={mockContext}
      />
    );
  };
}

// OBJECTIFS_KPI_TI
export const ObjectifsKPIModal = createDocumentModal('OBJECTIFS_KPI_TI', {
  esprit: {
    heroTitle: "L'Esprit du Cadre d'Objectifs Stratégiques TI & KPIs",
    heroDescription: "Le Cadre d'Objectifs TI & KPIs est le <strong>système de mesure</strong> qui aligne les objectifs TI sur les objectifs d'affaires. C'est l'outil qui permet de <strong>prouver la valeur</strong> de la TI et de piloter la performance.",
    pourquoi: {
      probleme: "Sans objectifs clairs et KPIs mesurables, la TI est perçue comme un <strong>centre de coût</strong> plutôt qu'un levier de valeur. Impossible de démontrer le ROI ou d'améliorer la performance.",
      solution: "Le Cadre d'Objectifs & KPIs établit des <strong>objectifs mesurables</strong> alignés sur les affaires, avec des indicateurs clairs pour suivre la progression et démontrer la valeur."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si la TI est une <strong>voiture</strong>, les KPIs sont le <strong>tableau de bord</strong> qui montre:",
      points: [
        "La vitesse (performance des systèmes)",
        "Le niveau d'essence (utilisation des ressources)",
        "Les alertes (problèmes à résoudre)",
        "La destination (objectifs à atteindre)"
      ],
      conclusion: "Le Cadre d'Objectifs & KPIs, c'est exactement ça: le <strong>tableau de bord</strong> qui permet de piloter la TI de manière stratégique."
    },
    piliers: [
      { icon: Target, title: '1. Objectifs Stratégiques', desc: 'Objectifs TI alignés sur les objectifs d\'affaires', color: 'indigo' },
      { icon: BarChart, title: '2. KPIs par Pilier', desc: 'Indicateurs clés pour chaque pilier TI', color: 'blue' },
      { icon: Calendar, title: '3. Fréquences de Suivi', desc: 'Quand et comment mesurer chaque KPI', color: 'purple' },
      { icon: UsersIcon, title: '4. Responsabilités', desc: 'Qui pilote chaque objectif et KPI', color: 'emerald' },
      { icon: TrendingUp, title: '5. Amélioration Continue', desc: 'Processus pour ajuster et améliorer', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Alignement affaires/TI', desc: 'Chaque objectif TI sert un objectif d\'affaires concret' },
      { title: 'KPIs par pilier BZ', desc: 'Organisé selon les 7 piliers pour une vue complète' },
      { title: 'Mesure de la valeur', desc: 'Démontre le ROI et la contribution de la TI' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au dashboard complet (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Cadre d'Objectifs & KPIs?",
    heroDescription: "Ce document s'adresse principalement à la <strong>direction</strong> et aux <strong>équipes TI</strong> qui doivent piloter et mesurer la performance.",
    profils: [
      {
        title: 'Direction Générale',
        icon: Star,
        color: 'indigo',
        scenarios: [
          'Comprendre la valeur de la TI',
          'Suivre la progression stratégique',
          'Prendre des décisions basées sur des données',
          'Justifier les investissements TI'
        ],
        niveau: 'Niveau 2-3 recommandé'
      },
      {
        title: 'DTI / Responsable TI',
        icon: Target,
        color: 'blue',
        scenarios: [
          'Piloter la performance TI',
          'Communiquer la valeur à la direction',
          'Identifier les axes d\'amélioration',
          'Gérer les attentes'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Comité TI / Gouvernance',
        icon: Shield,
        color: 'emerald',
        scenarios: [
          'Validation des objectifs',
          'Suivi des KPIs',
          'Décisions stratégiques',
          'Amélioration continue'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
    personas: [
      { role: 'Direction Générale', focus: 'Valeur, ROI, progression', icon: Eye },
      { role: 'DTI / Responsable TI', focus: 'Pilotage, performance, amélioration', icon: Zap },
      { role: 'Comité TI', focus: 'Gouvernance, décisions, suivi', icon: BarChart },
    ],
  },
  quand: {
    heroTitle: "Quand et pourquoi le client en a besoin?",
    heroDescription: "Le Cadre d'Objectifs & KPIs est nécessaire lorsque le client doit <strong>prouver la valeur</strong> de la TI et <strong>piloter la performance</strong> de manière structurée.",
    questions: [
      {
        question: 'Votre direction comprend-elle la valeur de la TI?',
        siNon: '→ Le Cadre d\'Objectifs & KPIs démontre la contribution de la TI aux affaires'
      },
      {
        question: 'Avez-vous des difficultés à justifier vos budgets TI?',
        siNon: '→ Les KPIs prouvent le ROI et la valeur générée'
      },
      {
        question: 'Pilotez-vous la TI de manière réactive plutôt que proactive?',
        siNon: '→ Le Cadre établit des objectifs clairs et un suivi structuré'
      },
      {
        question: 'Votre équipe TI manque-t-elle de visibilité sur sa performance?',
        siNon: '→ Les KPIs donnent une vision claire de la performance par pilier'
      },
      {
        question: 'Avez-vous besoin d\'améliorer continuellement la TI?',
        siNon: '→ Le Cadre établit un processus d\'amélioration continue basé sur les données'
      },
    ],
    signaux: [
      'Direction ne comprend pas la valeur TI',
      'Difficulté à justifier les budgets',
      'Pas de mesure de performance TI',
      'Décisions basées sur l\'intuition plutôt que les données',
      'Manque de visibilité sur la progression',
      'Besoin d\'amélioration continue structurée'
    ],
    timing: [
      { timing: 'Après un Plan Directeur', raison: 'Pour établir les objectifs et KPIs de la vision' },
      { timing: 'Début d\'année fiscale', raison: 'Pour définir les objectifs annuels' },
      { timing: 'Lors d\'une transformation TI', raison: 'Pour mesurer la progression' },
      { timing: 'Avant un budget TI', raison: 'Pour justifier les investissements' },
    ],
  },
  valeur: {
    heroTitle: "Comment expliquer la valeur au client?",
    heroDescription: "Le Cadre d'Objectifs & KPIs transforme la TI d'un <strong>centre de coût</strong> en un <strong>levier de valeur mesurable</strong>.",
    benefices: [
      {
        benefice: 'Démontrer la valeur TI',
        detail: 'Prouver le ROI et la contribution de la TI aux objectifs d\'affaires',
        icon: TrendingUp,
        color: 'indigo'
      },
      {
        benefice: 'Pilotage basé sur données',
        detail: 'Décisions stratégiques basées sur des KPIs mesurables plutôt que l\'intuition',
        icon: BarChart,
        color: 'blue'
      },
      {
        benefice: 'Amélioration continue',
        detail: 'Identifier les axes d\'amélioration et mesurer les progrès',
        icon: Target,
        color: 'emerald'
      },
      {
        benefice: 'Communication efficace',
        detail: 'Outil de communication avec la direction et les parties prenantes',
        icon: MessageSquare,
        color: 'purple'
      },
      {
        benefice: 'Justification des budgets',
        detail: 'Lier chaque investissement TI à un objectif mesurable',
        icon: DollarSign,
        color: 'orange'
      },
      {
        benefice: 'Alignement stratégique',
        detail: 'S\'assurer que chaque objectif TI sert un objectif d\'affaires',
        icon: Rocket,
        color: 'pink'
      }
    ],
    roi: {
      title: 'Retour sur investissement (ROI)',
      content: 'Un Cadre d\'Objectifs & KPIs bien structuré peut générer un ROI de 150-300% en:',
      points: [
        { label: 'Justification des budgets', value: 'Éviter les coupes injustifiées = économie de 50-100K$/an' },
        { label: 'Amélioration de la performance', value: 'Optimisation basée sur données = gain d\'efficacité de 15-25%' },
        { label: 'Réduction des irritants', value: 'Identification proactive des problèmes = économie de 30-50K$/an' },
        { label: 'Meilleure communication', value: 'Direction comprend la valeur = investissements justifiés' }
      ],
      cout: 'Coût d\'un Cadre d\'Objectifs & KPIs PRIsME: 3-10K$ (selon niveau)',
      valeur: 'Valeur générée: 30-100K$+ en économies et meilleure gouvernance'
    },
    arguments: [
      {
        persona: 'Pour la Direction Générale',
        arguments: [
          'Comprendre la valeur de la TI',
          'Suivre la progression vers les objectifs',
          'Prendre des décisions basées sur des données',
          'Justifier les investissements TI'
        ]
      },
      {
        persona: 'Pour le DTI / Responsable TI',
        arguments: [
          'Piloter la performance de manière structurée',
          'Communiquer la valeur à la direction',
          'Identifier les axes d\'amélioration',
          'Gérer les attentes avec des données'
        ]
      },
    ],
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité",
    heroDescription: "PRIsME offre <strong>5 niveaux</strong> de Cadre d'Objectifs & KPIs, du résumé exécutif au dashboard complet. Chaque niveau s'adapte à un besoin spécifique.",
    comparisonTable: [
      { critere: 'Pages (approximatif)', values: ['2-4', '6-10', '15-25', '30-45', '50-70'] },
      { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + DTI', 'Direction + Comité TI', 'Comité TI', 'Gouvernance complète'] },
      { critere: 'Nombre d\'objectifs', values: ['3-5', '5-8', '8-12', '12-18', '18-25'] },
      { critere: 'Nombre de KPIs', values: ['5-10', '10-15', '15-25', '25-35', '35-50'] },
      { critere: 'Détail par pilier', values: ['Mentionné', 'Basique', 'Moyen', 'Détaillé', 'Très détaillé'] },
      { critere: 'Fréquences de suivi', values: ['Annuel', 'Semestriel', 'Trimestriel', 'Mensuel', 'Hebdomadaire'] },
      { critere: 'Dashboard', values: ['Aucun', 'Simple', 'Basique', 'Détaillé', 'Complet avec scénarios'] },
      { critere: 'Prix indicatif', values: ['3-5K$', '5-8K$', '8-12K$', '12-18K$', '18-25K$'] },
    ],
    niveauDetails: [
      {
        inclus: [
          'Résumé exécutif (1-2 pages)',
          '3-5 objectifs stratégiques principaux',
          '5-10 KPIs clés',
          'Fréquences de suivi annuelles',
          'Langage simple, accessible'
        ],
        quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, direction non technique.'
      },
      {
        inclus: [
          'Résumé exécutif + sections standardisées',
          '5-8 objectifs stratégiques',
          '10-15 KPIs organisés par pilier',
          'Fréquences de suivi semestrielles',
          'Responsabilités identifiées',
          'Cibles basiques'
        ],
        quandUtiliser: 'Pour PME standard, besoin de structuration, première approche formelle de la mesure de performance.'
      },
      {
        inclus: [
          'Tout le niveau 2, PLUS:',
          '8-12 objectifs stratégiques détaillés',
          '15-25 KPIs avec cibles précises',
          'Fréquences de suivi trimestrielles',
          'Responsabilités détaillées',
          'Intégration ITIL/COBIT'
        ],
        quandUtiliser: 'Pour PME en croissance, besoin complet, alimenter gouvernance, équipe TI formelle.'
      },
      {
        inclus: [
          'Tout le niveau 3, PLUS:',
          '12-18 objectifs avec liens affaires',
          '25-35 KPIs très détaillés',
          'Fréquences de suivi mensuelles',
          'Dashboard de suivi',
          'Processus d\'amélioration continue',
          'Ton très professionnel, comité-ready'
        ],
        quandUtiliser: 'Pour entreprises matures, gouvernance importante, pilotage actif, comité TI formel.'
      },
      {
        inclus: [
          'Tout le niveau 4, PLUS:',
          '18-25 objectifs avec scénarios',
          '35-50 KPIs avec benchmarks',
          'Fréquences de suivi hebdomadaires',
          'Dashboard exécutif complet',
          'ROI estimé par objectif',
          'Document gouvernance-ready',
          'Ton exécutif, très visuel'
        ],
        quandUtiliser: 'Pour grandes entreprises, transformation majeure, gouvernance complète, présentation CA.'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Enjeux TI: vieillissement des serveurs, sauvegardes fragiles, cybersécurité minimale, irritants utilisateurs (lenteur, instabilité).
Plan Directeur: Vision 3-5 ans établie, besoin de cadrer les objectifs et KPIs pour mesurer la progression.`);

// CAHIER_CHARGE
export const CahierChargeModal = createDocumentModal('CAHIER_CHARGE', {
  esprit: {
    heroTitle: "L'Esprit des Spécifications d'exigences d'affaires et TI",
    heroDescription: "Les Spécifications d'exigences d'affaires et TI sont le <strong>document de référence</strong> qui définit précisément ce qui doit être livré, comment, et selon quels critères. C'est le contrat entre les besoins et la solution.",
    pourquoi: {
      probleme: "Sans spécifications d'exigences claires, les projets TI sont <strong>mal définis</strong>, les attentes floues, les livrables imprécis. On découvre les problèmes trop tard, les budgets explosent.",
      solution: "Les Spécifications d'exigences d'affaires et TI établissent un <strong>périmètre précis</strong>, des exigences claires, des critères d'acceptation mesurables, évitant les malentendus et les dépassements."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si vous commandez une <strong>maison sur mesure</strong>, vous avez besoin de <strong>spécifications d'exigences</strong> qui précisent:",
      points: [
        "Le nombre de pièces et leur fonction (périmètre)",
        "Les matériaux et finitions (exigences techniques)",
        "Les critères de qualité (critères d'acceptation)",
        "Les contraintes (budget, délais, réglementation)"
      ],
      conclusion: "Les Spécifications d'exigences d'affaires et TI, c'est exactement ça: le <strong>document de référence</strong> qui garantit que la solution livrée correspond exactement aux besoins."
    },
    piliers: [
      { icon: FileCheck, title: '1. Périmètre & Contexte', desc: 'Ce qui est inclus et exclu du projet', color: 'blue' },
      { icon: Target, title: '2. Exigences Fonctionnelles', desc: 'Ce que le système doit faire', color: 'indigo' },
      { icon: Shield, title: '3. Exigences Techniques', desc: 'Comment le système doit être construit', color: 'purple' },
      { icon: CheckCircle2, title: '4. Critères d\'Acceptation', desc: 'Comment valider que c\'est conforme', color: 'emerald' },
      { icon: AlertTriangle, title: '5. Contraintes & Risques', desc: 'Limites et risques à gérer', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Dérivé de la prise de besoin', desc: 'S\'appuie directement sur les besoins identifiés, garantissant l\'alignement' },
      { title: 'Périmètre clair', desc: 'Définit précisément ce qui est inclus et exclu, évitant les dépassements' },
      { title: 'Critères mesurables', desc: 'Exigences et critères d\'acceptation clairs et mesurables' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au document technique complet (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer les Spécifications d'exigences d'affaires et TI?",
    heroDescription: "Les Spécifications d'exigences d'affaires et TI s'adressent principalement aux <strong>équipes projet</strong>, aux <strong>fournisseurs</strong> et à la <strong>direction</strong> qui doivent comprendre et valider les exigences.",
    profils: [
      {
        title: 'Équipe Projet TI',
        icon: UsersIcon,
        color: 'blue',
        scenarios: [
          'Définir les exigences du projet',
          'Sélectionner les fournisseurs',
          'Valider les livrables',
          'Gérer les changements de périmètre'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Fournisseurs / Partenaires',
        icon: Building2,
        color: 'indigo',
        scenarios: [
          'Comprendre les exigences',
          'Soumettre des propositions',
          'Livrer selon les spécifications',
          'Valider les critères d\'acceptation'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Direction / Comité Projet',
        icon: Star,
        color: 'purple',
        scenarios: [
          'Valider le périmètre et le budget',
          'Comprendre les risques',
          'Approuver les critères d\'acceptation',
          'Prendre des décisions'
        ],
        niveau: 'Niveau 2-3 recommandé'
      },
    ],
    personas: [
      { role: 'Chef de Projet', focus: 'Périmètre, exigences, critères d\'acceptation', icon: Target },
      { role: 'Fournisseur', focus: 'Exigences techniques, contraintes, livrables', icon: FileCheck },
      { role: 'Direction', focus: 'Budget, risques, périmètre', icon: Eye },
    ],
  },
  quand: {
    heroTitle: "Quand et pourquoi le client en a besoin?",
    heroDescription: "Les Spécifications d'exigences d'affaires et TI sont nécessaires lorsque le client doit <strong>définir précisément</strong> un projet TI avant de le lancer ou de sélectionner un fournisseur.",
    questions: [
      {
        question: 'Avez-vous un projet TI à lancer mais les exigences sont floues?',
        siNon: "→ Les Spécifications d'exigences d'affaires et TI définissent précisément ce qui doit être livré"
      },
      {
        question: 'Vos projets TI dépassent-ils souvent le budget ou les délais?',
        siNon: "→ Les Spécifications d'exigences établissent un périmètre clair et des critères mesurables"
      },
      {
        question: 'Avez-vous besoin de sélectionner un fournisseur?',
        siNon: "→ Les Spécifications d'exigences permettent de comparer les propositions de manière objective"
      },
      {
        question: 'Y a-t-il des malentendus fréquents sur les livrables?',
        siNon: "→ Les Spécifications d'exigences éliminent les ambiguïtés avec des critères clairs"
      },
      {
        question: 'Avez-vous des difficultés à valider que le projet est terminé?',
        siNon: "→ Les Spécifications d'exigences définissent des critères d'acceptation mesurables"
      },
    ],
    signaux: [
      'Projets TI mal définis',
      'Dépassements de budget fréquents',
      'Malentendus sur les livrables',
      'Difficulté à valider les projets',
      'Besoin de sélectionner un fournisseur',
      'Changements de périmètre non gérés'
    ],
    timing: [
      { timing: 'Avant de lancer un projet', raison: 'Pour définir précisément le périmètre et les exigences' },
      { timing: 'Avant de sélectionner un fournisseur', raison: 'Pour comparer les propositions de manière objective' },
      { timing: 'Lors d\'un appel d\'offres', raison: 'Pour documenter les exigences et critères' },
      { timing: 'Lors d\'un changement majeur', raison: 'Pour redéfinir le périmètre et les exigences' },
    ],
  },
  valeur: {
    heroTitle: "Comment expliquer la valeur au client?",
    heroDescription: "Les Spécifications d'exigences d'affaires et TI ne sont pas juste une liste d'exigences, c'est un <strong>outil de gestion de projet</strong> qui évite les dépassements et garantit la qualité.",
    benefices: [
      {
        benefice: 'Périmètre clair',
        detail: 'Savoir exactement ce qui est inclus et exclu, évitant les dépassements',
        icon: Target,
        color: 'indigo'
      },
      {
        benefice: 'Exigences précises',
        detail: 'Définir clairement ce qui doit être livré, comment, et selon quels critères',
        icon: FileCheck,
        color: 'blue'
      },
      {
        benefice: 'Critères mesurables',
        detail: 'Valider que le projet est terminé avec des critères d\'acceptation clairs',
        icon: CheckCircle2,
        color: 'emerald'
      },
      {
        benefice: 'Réduction des risques',
        detail: 'Identifier les contraintes et risques avant qu\'ils ne deviennent des problèmes',
        icon: Shield,
        color: 'orange'
      },
      {
        benefice: 'Gestion des changements',
        detail: 'Base de référence pour gérer les changements de périmètre',
        icon: AlertTriangle,
        color: 'red'
      },
      {
        benefice: 'Sélection objective',
        detail: 'Comparer les propositions de fournisseurs de manière objective',
        icon: BarChart,
        color: 'purple'
      }
    ],
    roi: {
      title: 'Retour sur investissement (ROI)',
      content: "Des Spécifications d'exigences d'affaires et TI bien structurées peuvent générer un ROI de 300-500% en:",
      points: [
        { label: 'Réduction des dépassements', value: 'Éviter les dépassements de budget = économie de 50-150K$/projet' },
        { label: 'Réduction des malentendus', value: 'Éliminer les ambiguïtés = économie de temps de 20-30%' },
        { label: 'Meilleure sélection', value: 'Choisir le bon fournisseur = économie de 30-80K$/projet' },
        { label: 'Validation efficace', value: 'Critères clairs = réduction des retours de 40-60%' }
      ],
      cout: "Coût des Spécifications d'exigences d'affaires et TI PRIsME: 5-15K$ (selon niveau)",
      valeur: 'Valeur générée: 50-200K$+ en économies par projet'
    },
    arguments: [
      {
        persona: 'Pour la Direction',
        arguments: [
          'Périmètre clair et budget maîtrisé',
          'Risques identifiés et gérés',
          'Validation objective des livrables',
          'Réduction des dépassements'
        ]
      },
      {
        persona: 'Pour le Chef de Projet',
        arguments: [
          'Exigences précises et mesurables',
          'Base de référence pour gérer les changements',
          'Outil de communication avec les fournisseurs',
          'Critères d\'acceptation clairs'
        ]
      },
      {
        persona: 'Pour les Fournisseurs',
        arguments: [
          'Compréhension claire des exigences',
          'Proposition adaptée aux besoins',
          'Livraison conforme aux attentes',
          'Validation objective'
        ]
      }
    ],
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité",
    heroDescription: "PRIsME offre <strong>5 niveaux</strong> de Spécifications d'exigences d'affaires et TI, du résumé exécutif au document technique complet. Chaque niveau s'adapte à un besoin spécifique.",
    comparisonTable: [
      { critere: 'Pages (approximatif)', values: ['3-5', '8-12', '15-30', '35-50', '60-90'] },
      { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + Chef projet', 'Équipe projet', 'Équipe + Fournisseurs', 'Technique complet'] },
      { critere: 'Détail exigences', values: ['Liste simple', 'Descriptions basiques', 'Détails moyens', 'Très détaillées', 'Complètes avec spécifications'] },
      { critere: 'Exigences techniques', values: ['Mentionnées', 'Basiques', 'Moyennes', 'Détaillées', 'Très détaillées'] },
      { critere: 'Critères d\'acceptation', values: ['Liste simple', 'Basiques', 'Détaillés', 'Très détaillés', 'Complets avec tests'] },
      { critere: 'Contraintes & Risques', values: ['Mentionnés', 'Liste simple', 'Analyse basique', 'Analyse complète', 'Analyse stratégique + mitigation'] },
      { critere: 'Prix indicatif', values: ['5-8K$', '8-12K$', '12-18K$', '18-25K$', '25-35K$'] },
    ],
    niveauDetails: [
      {
        inclus: [
          'Résumé exécutif (1-2 pages)',
          'Périmètre et contexte',
          'Exigences principales',
          'Critères d\'acceptation basiques',
          'Langage simple, accessible'
        ],
        quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, projet simple.'
      },
      {
        inclus: [
          'Résumé exécutif + sections standardisées',
          'Périmètre détaillé',
          'Exigences fonctionnelles basiques',
          'Exigences techniques basiques',
          'Critères d\'acceptation détaillés',
          'Contraintes identifiées'
        ],
        quandUtiliser: 'Pour PME standard, projet moyen, besoin de structuration, première approche formelle.'
      },
      {
        inclus: [
          'Tout le niveau 2, PLUS:',
          'Exigences fonctionnelles détaillées',
          'Exigences techniques détaillées',
          'Critères d\'acceptation très détaillés',
          'Risques et dépendances',
          'Intégration ITIL/COBIT'
        ],
        quandUtiliser: 'Pour PME en croissance, projet complexe, besoin complet, équipe projet formelle.'
      },
      {
        inclus: [
          'Tout le niveau 3, PLUS:',
          'Exigences très détaillées avec spécifications',
          'Architecture technique',
          'Plan de tests',
          'Gestion des risques',
          'Plan de changement',
          'Ton très professionnel, fournisseur-ready'
        ],
        quandUtiliser: 'Pour entreprises matures, projet majeur, appel d\'offres, fournisseurs multiples.'
      },
      {
        inclus: [
          'Tout le niveau 4, PLUS:',
          'Spécifications techniques complètes',
          'Plan de tests détaillé',
          'Scénarios de validation',
          'Gestion avancée des risques',
          'Document technique complet',
          'Ton exécutif, très visuel'
        ],
        quandUtiliser: 'Pour grandes entreprises, projet critique, appel d\'offres complexe, validation technique approfondie.'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Projet: Modernisation du système de gestion de production avec intégration IoT et analytics.
Besoins: Système en temps réel, tableau de bord analytics, intégration avec ERP existant, conformité Loi 25.`);

// MATRICE_RACI_TI
export const MatriceRACIModal = createDocumentModal('MATRICE_RACI_TI', {
  esprit: {
    heroTitle: "L'Esprit de la Matrice RACI TI",
    heroDescription: "La Matrice RACI TI est le <strong>document de gouvernance</strong> qui définit clairement qui fait quoi dans la gestion TI. C'est l'outil qui élimine les zones grises et garantit la responsabilisation.",
    pourquoi: {
      probleme: "Sans matrice RACI claire, les responsabilités sont <strong>floues</strong>, les décisions traînent, les tâches tombent entre les chaises. Personne ne sait qui est responsable de quoi.",
      solution: "La Matrice RACI établit des <strong>responsabilités claires</strong> pour chaque activité TI, éliminant les ambiguïtés et garantissant que chaque tâche a un propriétaire."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si la gestion TI est une <strong>équipe de football</strong>, la Matrice RACI est le <strong>plan de jeu</strong> qui définit:",
      points: [
        "Qui marque les buts (Responsable)",
        "Qui prend les décisions (Accountable)",
        "Qui est consulté (Consulted)",
        "Qui est informé (Informed)"
      ],
      conclusion: "La Matrice RACI TI, c'est exactement ça: le <strong>plan de jeu</strong> qui garantit que chacun sait son rôle et que rien ne tombe entre les chaises."
    },
    piliers: [
      { icon: UsersIcon, title: '1. Rôles & Responsabilités', desc: 'Qui fait quoi dans la gouvernance TI', color: 'emerald' },
      { icon: Target, title: '2. Activités par Pilier', desc: 'Toutes les activités TI organisées par pilier', color: 'indigo' },
      { icon: CheckCircle2, title: '3. Assignations RACI', desc: 'R, A, C, I clairement définis pour chaque activité', color: 'blue' },
      { icon: Layers, title: '4. Niveaux (Stratégique/Tactique/Opérationnel)', desc: 'Profondeur de chaque activité', color: 'purple' },
      { icon: Shield, title: '5. Gouvernance Structurée', desc: 'Processus de décision et d\'escalade', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Gouvernance claire', desc: 'Définit précisément qui est responsable de quoi, éliminant les zones grises' },
      { title: 'Par piliers BZ', desc: 'Organisée selon les 7 piliers pour une vue complète' },
      { title: 'Niveaux de profondeur', desc: 'Distinction Stratégique/Tactique/Opérationnel pour chaque activité' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la matrice complète (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Matrice RACI TI?",
    heroDescription: "La Matrice RACI s'adresse principalement à la <strong>gouvernance TI</strong>, aux <strong>équipes</strong> et à la <strong>direction</strong> qui doivent comprendre les responsabilités.",
    profils: [
      {
        title: 'Comité TI / Gouvernance',
        icon: Shield,
        color: 'emerald',
        scenarios: [
          'Structurer la gouvernance TI',
          'Définir les responsabilités',
          'Éliminer les zones grises',
          'Améliorer la prise de décision'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'DTI / Responsable TI',
        icon: Target,
        color: 'indigo',
        scenarios: [
          'Clarifier les responsabilités de l\'équipe',
          'Gérer les interactions avec BZ',
          'Structurer la gouvernance',
          'Améliorer l\'efficacité'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Équipe TI / BZ',
        icon: UsersIcon,
        color: 'blue',
        scenarios: [
          'Comprendre son rôle',
          'Savoir qui contacter',
          'Éviter les doublons',
          'Améliorer la collaboration'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
    ],
    personas: [
      { role: 'Comité TI', focus: 'Gouvernance, responsabilités, décisions', icon: Shield },
      { role: 'DTI / Responsable TI', focus: 'Rôles équipe, interactions, efficacité', icon: Target },
      { role: 'Équipe TI', focus: 'Mon rôle, qui contacter, collaboration', icon: UsersIcon },
    ],
  },
  quand: {
    heroTitle: "Quand et pourquoi le client en a besoin?",
    heroDescription: "La Matrice RACI est nécessaire lorsque le client doit <strong>structurer la gouvernance TI</strong> et <strong>clarifier les responsabilités</strong> entre les différentes parties prenantes.",
    questions: [
      {
        question: 'Y a-t-il des zones grises dans les responsabilités TI?',
        siNon: '→ La Matrice RACI définit clairement qui est responsable de quoi'
      },
      {
        question: 'Les décisions TI traînent-elles faute de savoir qui décide?',
        siNon: '→ La Matrice RACI identifie qui est Accountable pour chaque décision'
      },
      {
        question: 'Y a-t-il des doublons ou des tâches qui tombent entre les chaises?',
        siNon: '→ La Matrice RACI élimine les ambiguïtés et garantit la couverture'
      },
      {
        question: 'La collaboration entre BZ et le client est-elle floue?',
        siNon: '→ La Matrice RACI définit clairement les rôles de chacun'
      },
      {
        question: 'Avez-vous besoin de structurer la gouvernance TI?',
        siNon: '→ La Matrice RACI est la base d\'une gouvernance structurée'
      },
    ],
    signaux: [
      'Responsabilités floues',
      'Décisions qui traînent',
      'Tâches qui tombent entre les chaises',
      'Doublons ou conflits',
      'Manque de gouvernance structurée',
      'Collaboration difficile'
    ],
    timing: [
      { timing: 'Lors d\'une nouvelle gouvernance TI', raison: 'Pour structurer les responsabilités dès le départ' },
      { timing: 'Lors d\'un changement de partenaire', raison: 'Pour redéfinir les rôles avec le nouveau partenaire' },
      { timing: 'Lors d\'une réorganisation TI', raison: 'Pour clarifier les nouvelles responsabilités' },
      { timing: 'Lors de problèmes de gouvernance', raison: 'Pour résoudre les ambiguïtés et conflits' },
    ],
  },
  valeur: {
    heroTitle: "Comment expliquer la valeur au client?",
    heroDescription: "La Matrice RACI TI n'est pas juste un tableau, c'est un <strong>outil de gouvernance</strong> qui transforme le chaos en structure claire.",
    benefices: [
      {
        benefice: 'Responsabilités claires',
        detail: 'Savoir exactement qui est responsable de quoi, éliminant les zones grises',
        icon: Target,
        color: 'emerald'
      },
      {
        benefice: 'Décisions rapides',
        detail: 'Identifier qui est Accountable pour chaque décision, accélérant le processus',
        icon: Zap,
        color: 'indigo'
      },
      {
        benefice: 'Réduction des doublons',
        detail: 'Éliminer les tâches en double et les zones de chevauchement',
        icon: CheckCircle2,
        color: 'blue'
      },
      {
        benefice: 'Amélioration de la collaboration',
        detail: 'Comprendre qui contacter et quand, améliorant l\'efficacité',
        icon: UsersIcon,
        color: 'purple'
      },
      {
        benefice: 'Gouvernance structurée',
        detail: 'Base solide pour une gouvernance TI professionnelle',
        icon: Shield,
        color: 'orange'
      },
      {
        benefice: 'Réduction des conflits',
        detail: 'Éliminer les ambiguïtés qui causent des conflits',
        icon: AlertTriangle,
        color: 'red'
      }
    ],
    roi: {
      title: 'Retour sur investissement (ROI)',
      content: 'Une Matrice RACI bien structurée peut générer un ROI de 200-400% en:',
      points: [
        { label: 'Réduction des délais', value: 'Décisions plus rapides = économie de temps de 20-30%' },
        { label: 'Réduction des doublons', value: 'Éliminer les tâches en double = économie de 15-25%' },
        { label: 'Amélioration de l\'efficacité', value: 'Collaboration améliorée = gain d\'efficacité de 15-20%' },
        { label: 'Réduction des conflits', value: 'Éliminer les ambiguïtés = économie de temps de gestion de 10-15%' }
      ],
      cout: 'Coût d\'une Matrice RACI PRIsME: 4-12K$ (selon niveau)',
      valeur: 'Valeur générée: 40-100K$+ en économies et meilleure gouvernance'
    },
    arguments: [
      {
        persona: 'Pour la Direction',
        arguments: [
          'Gouvernance TI structurée et professionnelle',
          'Décisions plus rapides et claires',
          'Réduction des conflits et ambiguïtés',
          'Meilleure collaboration'
        ]
      },
      {
        persona: 'Pour le DTI / Responsable TI',
        arguments: [
          'Responsabilités claires pour l\'équipe',
          'Outil de communication avec BZ',
          'Gouvernance structurée',
          'Amélioration de l\'efficacité'
        ]
      },
    ],
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité",
    heroDescription: "PRIsME offre <strong>5 niveaux</strong> de Matrice RACI, du résumé exécutif à la matrice complète avec tous les détails. Chaque niveau s'adapte à un besoin spécifique.",
    comparisonTable: [
      { critere: 'Pages (approximatif)', values: ['2-4', '6-10', '15-25', '30-45', '50-70'] },
      { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + DTI', 'Comité TI', 'Gouvernance complète', 'Gouvernance exécutive'] },
      { critere: 'Nombre d\'activités', values: ['10-15', '20-30', '35-50', '50-70', '70-100'] },
      { critere: 'Détail par pilier', values: ['Mentionné', 'Basique', 'Moyen', 'Détaillé', 'Très détaillé'] },
      { critere: 'Niveaux (Strat/Tact/Op)', values: ['Mentionnés', 'Basiques', 'Détaillés', 'Très détaillés', 'Complets'] },
      { critere: 'Rôles couverts', values: ['3-4', '4-5', '5-6', '6-7', '7+ avec sous-rôles'] },
      { critere: 'Prix indicatif', values: ['4-6K$', '6-9K$', '9-15K$', '15-22K$', '22-30K$'] },
    ],
    niveauDetails: [
      {
        inclus: [
          'Résumé exécutif (1-2 pages)',
          '10-15 activités principales',
          'Rôles de base (R, A, C, I)',
          'Niveaux mentionnés',
          'Langage simple, accessible'
        ],
        quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, gouvernance simple.'
      },
      {
        inclus: [
          'Résumé exécutif + sections standardisées',
          '20-30 activités par pilier',
          'Rôles détaillés',
          'Niveaux basiques',
          'Responsabilités identifiées'
        ],
        quandUtiliser: 'Pour PME standard, besoin de structuration, première approche formelle de la gouvernance.'
      },
      {
        inclus: [
          'Tout le niveau 2, PLUS:',
          '35-50 activités très détaillées',
          'Rôles complets avec sous-rôles',
          'Niveaux détaillés (Strat/Tact/Op)',
          'Intégration ITIL/COBIT'
        ],
        quandUtiliser: 'Pour PME en croissance, besoin complet, alimenter gouvernance, comité TI formel.'
      },
      {
        inclus: [
          'Tout le niveau 3, PLUS:',
          '50-70 activités complètes',
          'Rôles très détaillés',
          'Niveaux très détaillés',
          'Processus de décision',
          'Ton très professionnel, comité-ready'
        ],
        quandUtiliser: 'Pour entreprises matures, gouvernance importante, pilotage actif, comité TI formel.'
      },
      {
        inclus: [
          'Tout le niveau 4, PLUS:',
          '70-100 activités exhaustives',
          'Rôles exhaustifs avec sous-rôles',
          'Niveaux complets avec détails',
          'Gouvernance exécutive',
          'Document CA-ready',
          'Ton exécutif, très visuel'
        ],
        quandUtiliser: 'Pour grandes entreprises, gouvernance complète, transformation majeure, présentation CA.'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Gouvernance: Besoin de structurer la gouvernance TI entre le client, BZ, et les parties prenantes.
Rôles: DTI client, Responsable TI, Responsables métiers, BZ Partenaire TI, BZ Opérations.`);

// PLAN_BUDGETAIRE_TI_N1
export const PlanBudgetaireModal = createDocumentModal('PLAN_BUDGETAIRE_TI_N1', {
  esprit: {
    heroTitle: "L'Esprit du Plan Budgétaire TI Annuel",
    heroDescription: "Le Plan Budgétaire TI est le <strong>document financier</strong> qui transforme la vision stratégique en <strong>investissements concrets</strong> pour l'année N+1. C'est le pont entre la stratégie et les ressources.",
    pourquoi: {
      probleme: "Sans plan budgétaire structuré, les investissements TI sont <strong>réactifs</strong>, les budgets mal alloués, les priorités floues. On dépense sans vision, on coupe sans stratégie.",
      solution: "Le Plan Budgétaire établit un <strong>budget structuré</strong> par pilier, avec répartition CAPEX/OPEX, scénarios et liens avec la Roadmap, garantissant l'alignement stratégique."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si vous planifiez un <strong>voyage</strong>, vous avez besoin d'un <strong>budget</strong> qui montre:",
      points: [
        "Combien pour le transport (infrastructure)",
        "Combien pour l'hébergement (opérations)",
        "Combien pour les activités (projets)",
        "Combien pour les imprévus (risques)"
      ],
      conclusion: "Le Plan Budgétaire TI, c'est exactement ça: le <strong>budget structuré</strong> qui garantit que chaque dollar investi sert la stratégie."
    },
    piliers: [
      { icon: DollarSign, title: '1. Budget Global', desc: 'Budget total TI pour l\'année N+1', color: 'purple' },
      { icon: Target, title: '2. Répartition par Pilier', desc: 'Budget alloué à chaque pilier TI', color: 'indigo' },
      { icon: BarChart, title: '3. CAPEX / OPEX', desc: 'Répartition investissements vs opérations', color: 'blue' },
      { icon: Calendar, title: '4. Scénarios', desc: 'Budget conservateur, réaliste, ambitieux', color: 'emerald' },
      { icon: TrendingUp, title: '5. Lien Stratégique', desc: 'Chaque investissement lié à un objectif', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Dérivé de la Roadmap', desc: 'S\'appuie directement sur la Roadmap 12-24 mois, garantissant la cohérence' },
      { title: 'Structuré par piliers BZ', desc: 'Organisé selon les 7 piliers pour une vue complète' },
      { title: 'Scénarios réalistes', desc: 'Plusieurs scénarios selon les contraintes et opportunités' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au budget détaillé (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Plan Budgétaire TI?",
    heroDescription: "Le Plan Budgétaire s'adresse principalement à la <strong>direction financière</strong>, à la <strong>direction générale</strong> et au <strong>comité TI</strong> qui doivent approuver et suivre les investissements.",
    profils: [
      {
        title: 'Direction Financière',
        icon: DollarSign,
        color: 'purple',
        scenarios: [
          'Planification budgétaire annuelle',
          'Validation des investissements',
          'Suivi des dépenses',
          'Justification des budgets'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Direction Générale / CA',
        icon: Star,
        color: 'indigo',
        scenarios: [
          'Approbation du budget TI',
          'Compréhension des investissements',
          'Décisions stratégiques',
          'Suivi de la progression'
        ],
        niveau: 'Niveau 2-5 recommandé'
      },
      {
        title: 'Comité TI',
        icon: Shield,
        color: 'emerald',
        scenarios: [
          'Validation des priorités budgétaires',
          'Approbation des investissements',
          'Suivi des dépenses',
          'Décisions de réallocation'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
    personas: [
      { role: 'Direction Financière', focus: 'Budget, CAPEX/OPEX, justification', icon: DollarSign },
      { role: 'Direction Générale', focus: 'Investissements, ROI, stratégie', icon: Eye },
      { role: 'Comité TI', focus: 'Priorités, allocation, suivi', icon: BarChart },
    ],
  },
  quand: {
    heroTitle: "Quand et pourquoi le client en a besoin?",
    heroDescription: "Le Plan Budgétaire est nécessaire lorsque le client doit <strong>planifier les investissements TI</strong> pour l'année N+1 de manière structurée et stratégique.",
    questions: [
      {
        question: 'Avez-vous besoin de planifier le budget TI pour l\'année prochaine?',
        siNon: '→ Le Plan Budgétaire structure les investissements de manière stratégique'
      },
      {
        question: 'Vos budgets TI sont-ils souvent coupés ou mal justifiés?',
        siNon: '→ Le Plan Budgétaire justifie chaque investissement avec la stratégie'
      },
      {
        question: 'Avez-vous de la difficulté à allouer le budget entre les piliers TI?',
        siNon: '→ Le Plan Budgétaire répartit le budget de manière équilibrée'
      },
      {
        question: 'Avez-vous besoin de justifier les investissements au CA?',
        siNon: '→ Le Plan Budgétaire lie chaque investissement à un objectif stratégique'
      },
      {
        question: 'Avez-vous une Roadmap mais pas de budget associé?',
        siNon: '→ Le Plan Budgétaire transforme la Roadmap en investissements concrets'
      },
    ],
    signaux: [
      'Budget TI mal planifié',
      'Investissements réactifs',
      'Difficulté à justifier les budgets',
      'Allocation budgétaire floue',
      'Besoin d\'approbation CA',
      'Roadmap sans budget'
    ],
    timing: [
      { timing: 'Avant le budget annuel', raison: 'Pour planifier les investissements de l\'année N+1' },
      { timing: 'Après une Roadmap', raison: 'Pour transformer la Roadmap en budget' },
      { timing: 'Lors d\'une présentation CA', raison: 'Pour justifier les investissements TI' },
      { timing: 'Début d\'année fiscale', raison: 'Pour structurer le budget de l\'année' },
    ],
  },
  valeur: {
    heroTitle: "Comment expliquer la valeur au client?",
    heroDescription: "Le Plan Budgétaire TI n'est pas juste un budget, c'est un <strong>outil stratégique</strong> qui transforme la vision en investissements justifiés.",
    benefices: [
      {
        benefice: 'Budget structuré',
        detail: 'Planifier les investissements de manière stratégique et équilibrée',
        icon: DollarSign,
        color: 'purple'
      },
      {
        benefice: 'Justification claire',
        detail: 'Lier chaque investissement à un objectif stratégique, facilitant l\'approbation',
        icon: Target,
        color: 'indigo'
      },
      {
        benefice: 'Répartition équilibrée',
        detail: 'Allouer le budget de manière équilibrée entre les piliers TI',
        icon: BarChart,
        color: 'blue'
      },
      {
        benefice: 'Scénarios réalistes',
        detail: 'Plusieurs scénarios selon les contraintes et opportunités',
        icon: Calendar,
        color: 'emerald'
      },
      {
        benefice: 'Suivi facilité',
        detail: 'Outil de suivi des dépenses vs budget prévu',
        icon: TrendingUp,
        color: 'orange'
      },
      {
        benefice: 'Décisions éclairées',
        detail: 'Prendre des décisions budgétaires basées sur la stratégie',
        icon: Eye,
        color: 'pink'
      }
    ],
    roi: {
      title: 'Retour sur investissement (ROI)',
      content: 'Un Plan Budgétaire bien structuré peut générer un ROI de 200-400% en:',
      points: [
        { label: 'Optimisation des investissements', value: 'Allocation efficace = économie de 50-100K$/an' },
        { label: 'Réduction des coupes injustifiées', value: 'Justification claire = protection du budget de 30-60K$/an' },
        { label: 'Meilleure planification', value: 'Planification proactive = économie de 40-80K$/an' },
        { label: 'Décisions éclairées', value: 'Décisions basées sur stratégie = ROI amélioré de 20-40%' }
      ],
      cout: 'Coût d\'un Plan Budgétaire PRIsME: 4-12K$ (selon niveau)',
      valeur: 'Valeur générée: 50-150K$+ en économies et meilleure allocation'
    },
    arguments: [
      {
        persona: 'Pour la Direction Financière',
        arguments: [
          'Planification budgétaire structurée',
          'Justification claire des investissements',
          'Suivi facilité des dépenses',
          'Répartition CAPEX/OPEX claire'
        ]
      },
      {
        persona: 'Pour la Direction Générale',
        arguments: [
          'Compréhension des investissements TI',
          'Lien avec la stratégie d\'affaires',
          'Décisions éclairées',
          'Approbation facilitée'
        ]
      },
    ],
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité",
    heroDescription: "PRIsME offre <strong>5 niveaux</strong> de Plan Budgétaire, du résumé exécutif au budget détaillé avec scénarios. Chaque niveau s'adapte à un besoin spécifique.",
    comparisonTable: [
      { critere: 'Pages (approximatif)', values: ['3-5', '8-12', '15-30', '35-50', '60-90'] },
      { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + Finances', 'Comité TI', 'Gouvernance complète', 'CA'] },
      { critere: 'Détail par pilier', values: ['Global', 'Basique', 'Moyen', 'Détaillé', 'Très détaillé'] },
      { critere: 'CAPEX/OPEX', values: ['Mentionné', 'Basique', 'Détaillé', 'Très détaillé', 'Complet avec scénarios'] },
      { critere: 'Scénarios', values: ['Aucun', '1 scénario', '2 scénarios', '3 scénarios', 'Scénarios multiples'] },
      { critere: 'Justification', values: ['Simple', 'Basique', 'Détaillée', 'Très détaillée', 'Complète avec ROI'] },
      { critere: 'Prix indicatif', values: ['4-6K$', '6-9K$', '9-15K$', '15-22K$', '22-30K$'] },
    ],
    niveauDetails: [
      {
        inclus: [
          'Résumé exécutif (1-2 pages)',
          'Budget global TI',
          'Répartition basique par pilier',
          'CAPEX/OPEX mentionné',
          'Langage simple, accessible'
        ],
        quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, direction non technique.'
      },
      {
        inclus: [
          'Résumé exécutif + sections standardisées',
          'Budget détaillé par pilier',
          'Répartition CAPEX/OPEX basique',
          '1 scénario',
          'Justification basique'
        ],
        quandUtiliser: 'Pour PME standard, besoin de structuration, première approche formelle du budget TI.'
      },
      {
        inclus: [
          'Tout le niveau 2, PLUS:',
          'Budget très détaillé par pilier',
          'Répartition CAPEX/OPEX détaillée',
          '2 scénarios',
          'Justification détaillée',
          'Intégration ITIL/COBIT'
        ],
        quandUtiliser: 'Pour PME en croissance, besoin complet, alimenter comité TI, équipe TI formelle.'
      },
      {
        inclus: [
          'Tout le niveau 3, PLUS:',
          'Budget exhaustif par pilier',
          'Répartition CAPEX/OPEX très détaillée',
          '3 scénarios',
          'Justification très détaillée',
          'Lien avec Roadmap',
          'Ton très professionnel, comité-ready'
        ],
        quandUtiliser: 'Pour entreprises matures, gouvernance importante, pilotage actif, comité TI formel.'
      },
      {
        inclus: [
          'Tout le niveau 4, PLUS:',
          'Budget exhaustif avec sous-détails',
          'Scénarios multiples avec analyse',
          'ROI estimé par investissement',
          'Benchmarks sectoriels',
          'Document CA-ready',
          'Ton exécutif, très visuel'
        ],
        quandUtiliser: 'Pour grandes entreprises, présentation CA, transformation majeure, gouvernance complète.'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Roadmap: Plan d'exécution 12-24 mois établi, besoin de transformer en budget N+1.
Budget disponible: 150K$ annuel (CAPEX + OPEX).`);

// PLAN_BUDGETAIRE_5_ANS
export const PlanBudgetaire5AnsModal = createDocumentModal('PLAN_BUDGETAIRE_5_ANS', {
  esprit: {
    heroTitle: "L'Esprit du Plan Budgétaire TI – Vision 5 ans",
    heroDescription: "Le Plan Budgétaire TI – Vision 5 ans est le <strong>document de planification pluriannuelle</strong> qui structure les investissements TI sur cinq ans. Il est généré à partir du formulaire « Prise de besoin - Plan budgétaire 5 ans » et reflète l'expertise BZ en plan budgétaire TI et direction TI.",
    pourquoi: {
      probleme: "Sans vision budgétaire pluriannuelle, les investissements TI sont <strong>courtermistes</strong>, les cycles de renouvellement mal anticipés, les priorités annuelles incohérentes.",
      solution: "Le Plan Budgétaire 5 ans établit un <strong>tableau par poste et par année</strong>, avec projets en cours et améliorations, pour aligner la stratégie TI sur une enveloppe pluriannuelle."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Comme un <strong>plan financier personnel</strong> sur 5 ans :",
      points: [
        "Quel budget par catégorie (infra, logiciels, services, postes)",
        "Quelle évolution année après année",
        "Quels projets en cours et améliorations envisagées"
      ],
      conclusion: "Le Plan Budgétaire 5 ans, c'est la <strong>vision financière TI</strong> qui permet à la direction de piloter les investissements sur le moyen terme."
    },
    piliers: [
      { icon: DollarSign, title: 'Tableau 5 ans', desc: 'Montants par poste et par année (2026–2030)', color: 'purple' },
      { icon: Target, title: 'Projets en cours', desc: 'Initiatives en cours et à considérer', color: 'indigo' },
      { icon: TrendingUp, title: 'Améliorations', desc: 'Réalisées et suggérées', color: 'emerald' },
    ],
    prismeAdvantages: [
      { title: 'Généré depuis le formulaire', desc: 'Document dérivé de la prise de besoin Plan budgétaire 5 ans' },
      { title: 'Expertise BZ', desc: 'BZ est la référence en plan budgétaire TI et direction TI' },
      { title: 'Vision pluriannuelle', desc: 'Budget structuré sur 5 ans pour la décision' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Plan Budgétaire 5 ans?",
    heroDescription: "Direction générale, direction financière et comité TI pour <strong>valider la vision budgétaire pluriannuelle</strong> et les investissements prévus.",
    profils: [
      { title: 'Direction Générale', icon: Eye, color: 'purple', scenarios: ['Vision stratégique 5 ans', 'Enveloppe budgétaire', 'Priorités'], niveau: 'Tous niveaux' },
      { title: 'Direction Financière', icon: DollarSign, color: 'indigo', scenarios: ['Répartition par année', 'CAPEX/OPEX', 'Scénarios'], niveau: 'Niveau 2–4' },
      { title: 'Comité TI', icon: BarChart, color: 'emerald', scenarios: ['Détail par poste', 'Projets et améliorations', 'Suivi'], niveau: 'Niveau 3–5' },
    ],
    personas: [
      { role: 'Direction Générale', focus: 'Vision 5 ans, priorités', icon: Eye },
      { role: 'Direction Financière', focus: 'Budget, répartition', icon: DollarSign },
      { role: 'Comité TI', focus: 'Détail, suivi', icon: BarChart },
    ],
  },
  quand: {
    heroTitle: "Quand utiliser le Plan Budgétaire 5 ans?",
    heroDescription: "Lorsque le client doit <strong>présenter une vision budgétaire TI sur 5 ans</strong> (CA, banques, planification stratégique) à partir des données saisies dans le formulaire.",
    signaux: ['Planification pluriannuelle', 'Présentation CA', 'Demande banque/investisseur', 'Renouvellement infrastructure', 'Alignement stratégie/budget'],
    timing: [
      { timing: 'Après saisie du formulaire Plan 5 ans', raison: 'Générer le document à partir des données client' },
      { timing: 'Préparation budget pluriannuel', raison: 'Structurer la vision 5 ans' },
    ],
  },
  valeur: {
    heroTitle: "Valeur du Plan Budgétaire 5 ans",
    heroDescription: "Document <strong>professionnel et sur mesure</strong>, généré par l'IA à partir du formulaire, avec le positionnement expert BZ en plan budgétaire TI et direction TI.",
    benefices: [
      { benefice: 'Vision 5 ans', detail: 'Tableau budgétaire par poste et par année', icon: Calendar, color: 'purple' },
      { benefice: 'Données exactes', detail: 'Montants et textes issus du formulaire', icon: FileText, color: 'indigo' },
      { benefice: 'Expertise BZ', detail: 'Recommandations et ton expert', icon: Star, color: 'emerald' },
    ],
  },
});

// REGISTRE_AMELIORATION_CONTINUE_TI
export const RegistreAmeliorationModal = createDocumentModal('REGISTRE_AMELIORATION_CONTINUE_TI', {
  esprit: {
    heroTitle: "L'Esprit du Registre d'Amélioration Continue TI",
    heroDescription: "Le Registre d'Amélioration Continue est le <strong>backlog structuré</strong> qui capture toutes les améliorations TI à réaliser. C'est l'outil qui transforme les idées en actions prioritaires.",
    pourquoi: {
      probleme: "Sans registre structuré, les améliorations TI sont <strong>perdues</strong>, les priorités floues, les efforts dispersés. On réagit aux urgences au lieu d'améliorer de manière continue.",
      solution: "Le Registre d'Amélioration Continue organise les améliorations par <strong>priorité et effort</strong>, avec liens aux objectifs stratégiques, garantissant une amélioration continue structurée."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si la TI est une <strong>maison</strong>, le Registre d'Amélioration Continue est la <strong>liste de rénovations</strong> qui montre:",
      points: [
        "Les améliorations urgentes (priorité haute)",
        "Les améliorations importantes (priorité moyenne)",
        "Les améliorations souhaitables (priorité basse)",
        "L'effort nécessaire pour chaque amélioration"
      ],
      conclusion: "Le Registre d'Amélioration Continue TI, c'est exactement ça: la <strong>liste structurée</strong> qui garantit que les améliorations sont capturées, priorisées et réalisées."
    },
    piliers: [
      { icon: ListChecks, title: '1. Capture des Améliorations', desc: 'Toutes les améliorations identifiées et documentées', color: 'orange' },
      { icon: Target, title: '2. Priorisation', desc: 'Améliorations classées par priorité et impact', color: 'indigo' },
      { icon: BarChart, title: '3. Estimation d\'Effort', desc: 'Effort nécessaire pour chaque amélioration', color: 'blue' },
      { icon: Rocket, title: '4. Lien Stratégique', desc: 'Chaque amélioration liée à un objectif', color: 'emerald' },
      { icon: Calendar, title: '5. Planification', desc: 'Améliorations planifiées dans la Roadmap', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Backlog structuré', desc: 'Organise toutes les améliorations de manière structurée et priorisée' },
      { title: 'Par piliers BZ', desc: 'Organisé selon les 7 piliers pour une vue complète' },
      { title: 'Lien avec stratégie', desc: 'Chaque amélioration liée aux objectifs stratégiques' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au backlog complet (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Registre d'Amélioration Continue?",
    heroDescription: "Le Registre s'adresse principalement aux <strong>équipes TI</strong> et à la <strong>gouvernance</strong> qui doivent planifier et exécuter les améliorations.",
    profils: [
      {
        title: 'Équipe TI',
        icon: UsersIcon,
        color: 'orange',
        scenarios: [
          'Planifier les améliorations',
          'Prioriser les efforts',
          'Suivre la progression',
          'Gérer le backlog'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'DTI / Responsable TI',
        icon: Target,
        color: 'indigo',
        scenarios: [
          'Gérer le portefeuille d\'améliorations',
          'Allouer les ressources',
          'Communiquer avec la direction',
          'Piloter la progression'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Comité TI',
        icon: Shield,
        color: 'emerald',
        scenarios: [
          'Valider les priorités',
          'Approuver les améliorations',
          'Suivre la progression',
          'Décisions stratégiques'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
    personas: [
      { role: 'Équipe TI', focus: 'Backlog, priorités, planification', icon: ListChecks },
      { role: 'DTI / Responsable TI', focus: 'Portefeuille, ressources, progression', icon: Target },
      { role: 'Comité TI', focus: 'Priorités, validation, suivi', icon: Shield },
    ],
  },
  quand: {
    heroTitle: "Quand et pourquoi le client en a besoin?",
    heroDescription: "Le Registre est nécessaire lorsque le client doit <strong>structurer les améliorations TI</strong> et <strong>prioriser les efforts</strong> de manière continue.",
    questions: [
      {
        question: 'Avez-vous beaucoup d\'idées d\'amélioration mais pas de plan?',
        siNon: '→ Le Registre capture et organise toutes les améliorations'
      },
      {
        question: 'Vos améliorations sont-elles souvent oubliées ou perdues?',
        siNon: '→ Le Registre garantit que rien n\'est perdu'
      },
      {
        question: 'Avez-vous de la difficulté à prioriser les améliorations?',
        siNon: '→ Le Registre classe les améliorations par priorité et impact'
      },
      {
        question: 'Vos efforts d\'amélioration sont-ils dispersés?',
        siNon: '→ Le Registre organise les améliorations de manière structurée'
      },
      {
        question: 'Avez-vous besoin d\'amélioration continue structurée?',
        siNon: '→ Le Registre est la base d\'une amélioration continue professionnelle'
      },
    ],
    signaux: [
      'Idées d\'amélioration perdues',
      'Priorités floues',
      'Efforts dispersés',
      'Manque de planification',
      'Besoin d\'amélioration continue',
      'Backlog non structuré'
    ],
    timing: [
      { timing: 'Lors d\'une amélioration continue', raison: 'Pour structurer le processus d\'amélioration' },
      { timing: 'Après un Plan Directeur', raison: 'Pour capturer les améliorations de la vision' },
      { timing: 'Lors d\'une réorganisation TI', raison: 'Pour prioriser les améliorations nécessaires' },
      { timing: 'Début d\'année', raison: 'Pour planifier les améliorations de l\'année' },
    ],
  },
  valeur: {
    heroTitle: "Comment expliquer la valeur au client?",
    heroDescription: "Le Registre d'Amélioration Continue n'est pas juste une liste, c'est un <strong>outil de gestion</strong> qui transforme les idées en actions prioritaires.",
    benefices: [
      {
        benefice: 'Capture structurée',
        detail: 'Aucune amélioration n\'est perdue, tout est documenté et organisé',
        icon: ListChecks,
        color: 'orange'
      },
      {
        benefice: 'Priorisation claire',
        detail: 'Améliorations classées par priorité et impact, optimisant les efforts',
        icon: Target,
        color: 'indigo'
      },
      {
        benefice: 'Planification efficace',
        detail: 'Améliorations planifiées dans la Roadmap, garantissant l\'exécution',
        icon: Calendar,
        color: 'blue'
      },
      {
        benefice: 'Lien stratégique',
        detail: 'Chaque amélioration liée à un objectif stratégique, garantissant l\'alignement',
        icon: Rocket,
        color: 'emerald'
      },
      {
        benefice: 'Suivi de progression',
        detail: 'Suivre l\'avancement des améliorations, mesurant la progression',
        icon: BarChart,
        color: 'purple'
      },
      {
        benefice: 'Amélioration continue',
        detail: 'Processus structuré d\'amélioration continue, garantissant l\'évolution',
        icon: TrendingUp,
        color: 'pink'
      }
    ],
    roi: {
      title: 'Retour sur investissement (ROI)',
      content: 'Un Registre d\'Amélioration Continue bien structuré peut générer un ROI de 150-300% en:',
      points: [
        { label: 'Optimisation des efforts', value: 'Priorisation efficace = gain d\'efficacité de 20-30%' },
        { label: 'Réduction des oublis', value: 'Capture structurée = économie de temps de 15-25%' },
        { label: 'Meilleure planification', value: 'Planification efficace = économie de 25-40K$/an' },
        { label: 'Amélioration continue', value: 'Processus structuré = amélioration de performance de 15-20%' }
      ],
      cout: 'Coût d\'un Registre d\'Amélioration Continue PRIsME: 3-10K$ (selon niveau)',
      valeur: 'Valeur générée: 30-80K$+ en économies et meilleure performance'
    },
    arguments: [
      {
        persona: 'Pour l\'Équipe TI',
        arguments: [
          'Backlog structuré et priorisé',
          'Clarté sur les priorités',
          'Planification efficace',
          'Suivi de progression'
        ]
      },
      {
        persona: 'Pour le DTI / Responsable TI',
        arguments: [
          'Gestion du portefeuille d\'améliorations',
          'Allocation optimale des ressources',
          'Communication avec la direction',
          'Pilotage de la progression'
        ]
      },
    ],
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité",
    heroDescription: "PRIsME offre <strong>5 niveaux</strong> de Registre d'Amélioration Continue, du résumé exécutif au backlog complet. Chaque niveau s'adapte à un besoin spécifique.",
    comparisonTable: [
      { critere: 'Pages (approximatif)', values: ['2-4', '6-10', '15-25', '30-45', '50-70'] },
      { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + DTI', 'Équipe TI', 'Gouvernance complète', 'Gouvernance exécutive'] },
      { critere: 'Nombre d\'améliorations', values: ['10-15', '20-35', '40-60', '70-100', '100-150'] },
      { critere: 'Détail par pilier', values: ['Mentionné', 'Basique', 'Moyen', 'Détaillé', 'Très détaillé'] },
      { critere: 'Priorisation', values: ['Simple', 'Basique', 'Détaillée', 'Très détaillée', 'Complète avec scénarios'] },
      { critere: 'Estimation d\'effort', values: ['Mentionnée', 'Basique', 'Détaillée', 'Très détaillée', 'Complète'] },
      { critere: 'Prix indicatif', values: ['3-5K$', '5-8K$', '8-12K$', '12-18K$', '18-25K$'] },
    ],
    niveauDetails: [
      {
        inclus: [
          'Résumé exécutif (1-2 pages)',
          '10-15 améliorations principales',
          'Priorisation simple',
          'Estimation d\'effort mentionnée',
          'Langage simple, accessible'
        ],
        quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, amélioration simple.'
      },
      {
        inclus: [
          'Résumé exécutif + sections standardisées',
          '20-35 améliorations par pilier',
          'Priorisation basique',
          'Estimation d\'effort basique',
          'Lien avec objectifs'
        ],
        quandUtiliser: 'Pour PME standard, besoin de structuration, première approche formelle de l\'amélioration continue.'
      },
      {
        inclus: [
          'Tout le niveau 2, PLUS:',
          '40-60 améliorations détaillées',
          'Priorisation détaillée',
          'Estimation d\'effort détaillée',
          'Lien avec Roadmap',
          'Intégration ITIL/COBIT'
        ],
        quandUtiliser: 'Pour PME en croissance, besoin complet, alimenter planification, équipe TI formelle.'
      },
      {
        inclus: [
          'Tout le niveau 3, PLUS:',
          '70-100 améliorations très détaillées',
          'Priorisation très détaillée',
          'Estimation d\'effort très détaillée',
          'Planification dans Roadmap',
          'Ton très professionnel, comité-ready'
        ],
        quandUtiliser: 'Pour entreprises matures, gouvernance importante, pilotage actif, comité TI formel.'
      },
      {
        inclus: [
          'Tout le niveau 4, PLUS:',
          '100-150 améliorations exhaustives',
          'Priorisation complète avec scénarios',
          'Estimation d\'effort complète',
          'Planification détaillée',
          'Document gouvernance-ready',
          'Ton exécutif, très visuel'
        ],
        quandUtiliser: 'Pour grandes entreprises, transformation majeure, gouvernance complète, présentation CA.'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Améliorations identifiées: Modernisation infrastructure, amélioration sécurité, optimisation applications, meilleure gouvernance, formation équipe.
Plan Directeur: Vision 3-5 ans établie, besoin de structurer les améliorations à réaliser.`);

// REGISTRE_RISQUES_TI
export const RegistreRisquesModal = createDocumentModal('REGISTRE_RISQUES_TI', {
  esprit: {
    heroTitle: "L'Esprit du Registre des Risques TI",
    heroDescription: "Le Registre des Risques TI est le <strong>document de gestion des risques</strong> qui identifie, analyse et traite tous les risques TI. C'est l'outil qui transforme les menaces en opportunités de mitigation.",
    pourquoi: {
      probleme: "Sans gestion des risques structurée, les risques TI sont <strong>ignorés</strong> jusqu'à ce qu'ils deviennent des crises. On réagit aux incidents au lieu de les prévenir.",
      solution: "Le Registre des Risques identifie, analyse et traite les risques de manière <strong>proactive</strong>, avec plans de mitigation, garantissant la résilience TI."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si la TI est une <strong>maison</strong>, le Registre des Risques est le <strong>rapport d'inspection</strong> qui montre:",
      points: [
        "Les risques structurels (cybersécurité)",
        "Les risques d'incendie (pannes)",
        "Les risques d'inondation (perte de données)",
        "Les plans de mitigation (solutions)"
      ],
      conclusion: "Le Registre des Risques TI, c'est exactement ça: le <strong>rapport d'inspection</strong> qui garantit que les risques sont identifiés et traités avant qu'ils ne deviennent des crises."
    },
    piliers: [
      { icon: AlertTriangle, title: '1. Identification des Risques', desc: 'Tous les risques TI identifiés et documentés', color: 'red' },
      { icon: BarChart, title: '2. Analyse (Impact/Probabilité)', desc: 'Chaque risque analysé avec impact et probabilité', color: 'orange' },
      { icon: Target, title: '3. Niveau de Risque', desc: 'Risques classés par niveau (critique, élevé, moyen, faible)', color: 'indigo' },
      { icon: Shield, title: '4. Plan de Traitement', desc: 'Plan de mitigation pour chaque risque', color: 'emerald' },
      { icon: TrendingUp, title: '5. Suivi & Révision', desc: 'Processus de suivi et révision des risques', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Gestion proactive', desc: 'Identifie et traite les risques avant qu\'ils ne deviennent des crises' },
      { title: 'Par piliers BZ', desc: 'Organisé selon les 7 piliers pour une vue complète' },
      { title: 'Analyse structurée', desc: 'Chaque risque analysé avec impact, probabilité et niveau' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au registre complet (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Registre des Risques TI?",
    heroDescription: "Le Registre s'adresse principalement à la <strong>gouvernance TI</strong>, à la <strong>direction</strong> et aux <strong>équipes</strong> qui doivent gérer les risques.",
    profils: [
      {
        title: 'Comité TI / Gouvernance',
        icon: Shield,
        color: 'red',
        scenarios: [
          'Gérer les risques stratégiques',
          'Valider les plans de mitigation',
          'Prendre des décisions',
          'Suivre la progression'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Direction Générale',
        icon: Star,
        color: 'indigo',
        scenarios: [
          'Comprendre les risques TI',
          'Valider les investissements de mitigation',
          'Décisions stratégiques',
          'Gestion des risques d\'entreprise'
        ],
        niveau: 'Niveau 2-3 recommandé'
      },
      {
        title: 'DTI / Responsable TI',
        icon: Target,
        color: 'blue',
        scenarios: [
          'Gérer les risques opérationnels',
          'Implémenter les plans de mitigation',
          'Communiquer avec la direction',
          'Piloter la gestion des risques'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
    ],
    personas: [
      { role: 'Comité TI', focus: 'Risques stratégiques, décisions, validation', icon: Shield },
      { role: 'Direction Générale', focus: 'Risques d\'entreprise, investissements, stratégie', icon: Eye },
      { role: 'DTI / Responsable TI', focus: 'Risques opérationnels, mitigation, exécution', icon: Target },
    ],
  },
  quand: {
    heroTitle: "Quand et pourquoi le client en a besoin?",
    heroDescription: "Le Registre est nécessaire lorsque le client doit <strong>gérer les risques TI</strong> de manière structurée et proactive.",
    questions: [
      {
        question: 'Avez-vous des risques TI non gérés?',
        siNon: '→ Le Registre identifie et traite tous les risques de manière structurée'
      },
      {
        question: 'Avez-vous déjà subi des incidents TI majeurs?',
        siNon: '→ Le Registre prévient les incidents en gérant les risques proactivement'
      },
      {
        question: 'Votre direction demande-t-elle une gestion des risques?',
        siNon: '→ Le Registre démontre une gestion professionnelle des risques'
      },
      {
        question: 'Avez-vous besoin de conformité (Loi 25, ISO 27001)?',
        siNon: '→ Le Registre est un élément clé de la conformité'
      },
      {
        question: 'Vos investissements de sécurité sont-ils mal justifiés?',
        siNon: '→ Le Registre justifie les investissements avec l\'analyse des risques'
      },
    ],
    signaux: [
      'Risques TI non gérés',
      'Incidents TI récurrents',
      'Besoin de conformité',
      'Direction demande gestion des risques',
      'Investissements sécurité mal justifiés',
      'Manque de résilience TI'
    ],
    timing: [
      { timing: 'Lors d\'une évaluation des risques', raison: 'Pour structurer la gestion des risques' },
      { timing: 'Après un incident majeur', raison: 'Pour prévenir les récidives' },
      { timing: 'Lors d\'une conformité', raison: 'Pour démontrer la gestion des risques' },
      { timing: 'Avant un investissement sécurité', raison: 'Pour justifier les investissements' },
    ],
  },
  valeur: {
    heroTitle: "Comment expliquer la valeur au client?",
    heroDescription: "Le Registre des Risques TI n'est pas juste une liste, c'est un <strong>outil de protection</strong> qui transforme les menaces en opportunités de mitigation.",
    benefices: [
      {
        benefice: 'Gestion proactive',
        detail: 'Identifier et traiter les risques avant qu\'ils ne deviennent des crises',
        icon: Shield,
        color: 'red'
      },
      {
        benefice: 'Réduction des incidents',
        detail: 'Prévenir les incidents en gérant les risques proactivement',
        icon: AlertTriangle,
        color: 'orange'
      },
      {
        benefice: 'Justification des investissements',
        detail: 'Justifier les investissements de sécurité avec l\'analyse des risques',
        icon: DollarSign,
        color: 'indigo'
      },
      {
        benefice: 'Conformité',
        detail: 'Démontrer la gestion des risques pour la conformité (Loi 25, ISO 27001)',
        icon: CheckCircle2,
        color: 'emerald'
      },
      {
        benefice: 'Résilience TI',
        detail: 'Améliorer la résilience TI en gérant les risques de manière structurée',
        icon: TrendingUp,
        color: 'purple'
      },
      {
        benefice: 'Décisions éclairées',
        detail: 'Prendre des décisions basées sur l\'analyse des risques',
        icon: Eye,
        color: 'pink'
      }
    ],
    roi: {
      title: 'Retour sur investissement (ROI)',
      content: 'Un Registre des Risques bien structuré peut générer un ROI de 500-1000% en:',
      points: [
        { label: 'Prévention des incidents', value: 'Éviter un incident majeur = économie de 100-500K$+' },
        { label: 'Réduction des pertes', value: 'Gestion proactive = réduction des pertes de 50-200K$/an' },
        { label: 'Conformité', value: 'Éviter les amendes = économie de 25-100K$/an' },
        { label: 'Justification investissements', value: 'Investissements justifiés = ROI amélioré de 30-50%' }
      ],
      cout: 'Coût d\'un Registre des Risques PRIsME: 4-12K$ (selon niveau)',
      valeur: 'Valeur générée: 100-500K$+ en économies et protection'
    },
    arguments: [
      {
        persona: 'Pour la Direction',
        arguments: [
          'Gestion professionnelle des risques',
          'Protection de l\'entreprise',
          'Conformité réglementaire',
          'Décisions éclairées'
        ]
      },
      {
        persona: 'Pour le DTI / Responsable TI',
        arguments: [
          'Gestion proactive des risques',
          'Justification des investissements',
          'Réduction des incidents',
          'Amélioration de la résilience'
        ]
      },
    ],
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité",
    heroDescription: "PRIsME offre <strong>5 niveaux</strong> de Registre des Risques, du résumé exécutif au registre complet avec plans détaillés. Chaque niveau s'adapte à un besoin spécifique.",
    comparisonTable: [
      { critere: 'Pages (approximatif)', values: ['3-5', '8-12', '15-30', '35-50', '60-90'] },
      { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + DTI', 'Comité TI', 'Gouvernance complète', 'Gouvernance exécutive'] },
      { critere: 'Nombre de risques', values: ['10-15', '20-35', '40-60', '70-100', '100-150'] },
      { critere: 'Analyse (Impact/Prob)', values: ['Simple', 'Basique', 'Détaillée', 'Très détaillée', 'Complète avec scénarios'] },
      { critere: 'Plan de traitement', values: ['Mentionné', 'Basique', 'Détaillé', 'Très détaillé', 'Complet avec budget'] },
      { critere: 'Matrice de risques', values: ['Aucune', 'Simple', 'Basique', 'Détaillée', 'Complète'] },
      { critere: 'Prix indicatif', values: ['4-6K$', '6-9K$', '9-15K$', '15-22K$', '22-30K$'] },
    ],
    niveauDetails: [
      {
        inclus: [
          'Résumé exécutif (1-2 pages)',
          '10-15 risques principaux',
          'Analyse simple (Impact/Prob)',
          'Plan de traitement mentionné',
          'Langage simple, accessible'
        ],
        quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, risques simples.'
      },
      {
        inclus: [
          'Résumé exécutif + sections standardisées',
          '20-35 risques par pilier',
          'Analyse basique (Impact/Prob)',
          'Plan de traitement basique',
          'Matrice de risques simple'
        ],
        quandUtiliser: 'Pour PME standard, besoin de structuration, première approche formelle de la gestion des risques.'
      },
      {
        inclus: [
          'Tout le niveau 2, PLUS:',
          '40-60 risques détaillés',
          'Analyse détaillée (Impact/Prob)',
          'Plan de traitement détaillé',
          'Matrice de risques basique',
          'Intégration ITIL/COBIT/NIST'
        ],
        quandUtiliser: 'Pour PME en croissance, besoin complet, alimenter gouvernance, équipe TI formelle.'
      },
      {
        inclus: [
          'Tout le niveau 3, PLUS:',
          '70-100 risques très détaillés',
          'Analyse très détaillée',
          'Plan de traitement très détaillé',
          'Matrice de risques détaillée',
          'Ton très professionnel, comité-ready'
        ],
        quandUtiliser: 'Pour entreprises matures, gouvernance importante, pilotage actif, comité TI formel.'
      },
      {
        inclus: [
          'Tout le niveau 4, PLUS:',
          '100-150 risques exhaustifs',
          'Analyse complète avec scénarios',
          'Plan de traitement complet avec budget',
          'Matrice de risques complète',
          'Document gouvernance-ready',
          'Ton exécutif, très visuel'
        ],
        quandUtiliser: 'Pour grandes entreprises, transformation majeure, gouvernance complète, présentation CA.'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Risques identifiés: Cybersécurité faible, sauvegardes fragiles, dépendance fournisseurs, vieillissement infrastructure, perte de données, pannes système.
Conformité: Besoin de conformité Loi 25, gestion des risques requise.`);

// OFFRE_SERVICE_BZ
export const OffreServiceBZModal = createDocumentModal('OFFRE_SERVICE_BZ', {
  esprit: {
    heroTitle: "L'Esprit de l'Offre de Service BZ",
    heroDescription: "L'Offre de Service BZ est le <strong>document commercial</strong> qui présente 3 offres adaptées au budget client, basées sur le Cadre BZ. C'est l'outil qui transforme les besoins en solutions concrètes.",
    pourquoi: {
      probleme: "Sans offre structurée, les propositions commerciales sont <strong>génériques</strong>, les budgets flous, les bénéfices non démontrés. On vend des services au lieu de solutions.",
      solution: "L'Offre de Service BZ présente 3 offres <strong>adaptées au budget</strong>, avec visualisation AS-IS/TARGET, livrables tangibles et ROI, garantissant une proposition de valeur claire."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si vous achetez une <strong>voiture</strong>, vous avez besoin de voir les <strong>3 modèles</strong> qui correspondent à votre budget:",
      points: [
        "Le modèle Essentiel (fonctions de base)",
        "Le modèle Standard (équilibre qualité/prix)",
        "Le modèle Premium (toutes les fonctionnalités)"
      ],
      conclusion: "L'Offre de Service BZ, c'est exactement ça: les <strong>3 modèles</strong> qui permettent au client de choisir selon son budget et ses besoins."
    },
    piliers: [
      { icon: Star, title: '1. 3 Offres Adaptées', desc: 'Essentielle, Recommandée, Optimale selon le budget', color: 'indigo' },
      { icon: Target, title: '2. Analyse AS-IS/TARGET', desc: 'Visualisation de la progression avec chaque offre', color: 'blue' },
      { icon: Rocket, title: '3. Piliers & Tactiques', desc: 'Piliers BZ couverts et tactiques activées', color: 'emerald' },
      { icon: DollarSign, title: '4. Investissement & ROI', desc: 'Budget mensuel et horizon de retour', color: 'purple' },
      { icon: CheckCircle2, title: '5. Livrables Tangibles', desc: 'Livrables concrets par pilier et offre', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Basée sur le Cadre BZ', desc: 'S\'appuie sur les 7 piliers et 70 tactiques BZ, garantissant la cohérence' },
      { title: 'Adaptée au budget', desc: '3 offres adaptées au budget client, facilitant la décision' },
      { title: 'Visualisation claire', desc: 'Spider charts AS-IS/TARGET pour chaque offre' },
      { title: 'Proposition de valeur', desc: 'Démontre clairement la valeur et le ROI de chaque offre' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer l'Offre de Service BZ?",
    heroDescription: "L'Offre s'adresse principalement aux <strong>clients prospects</strong> et à la <strong>direction</strong> qui doivent choisir une solution TI adaptée à leur budget.",
    profils: [
      {
        title: 'Clients Prospects',
        icon: Building2,
        color: 'indigo',
        scenarios: [
          'Comprendre les offres BZ',
          'Choisir selon le budget',
          'Comparer les options',
          'Prendre une décision'
        ],
        niveau: 'Niveau 2-3 recommandé'
      },
      {
        title: 'Direction Générale Client',
        icon: Star,
        color: 'blue',
        scenarios: [
          'Valider l\'investissement',
          'Comprendre le ROI',
          'Approuver l\'offre',
          'Décision stratégique'
        ],
        niveau: 'Niveau 2-3 recommandé'
      },
      {
        title: 'Équipe Ventes BZ',
        icon: UsersIcon,
        color: 'emerald',
        scenarios: [
          'Présenter les offres',
          'Adapter au budget client',
          'Démontrer la valeur',
          'Fermer la vente'
        ],
        niveau: 'Tous niveaux'
      },
    ],
    personas: [
      { role: 'Client Prospect', focus: 'Offres, budget, valeur, ROI', icon: Eye },
      { role: 'Direction Client', focus: 'Investissement, ROI, décision', icon: Star },
      { role: 'Vendeur BZ', focus: 'Présentation, adaptation, fermeture', icon: UsersIcon },
    ],
  },
  quand: {
    heroTitle: "Quand et pourquoi le client en a besoin?",
    heroDescription: "L'Offre est nécessaire lorsque BZ doit <strong>présenter une proposition commerciale</strong> adaptée au budget et aux besoins du client.",
    questions: [
      {
        question: 'Avez-vous un client prospect qui demande une proposition?',
        siNon: '→ L\'Offre présente 3 options adaptées au budget'
      },
      {
        question: 'Le client a-t-il un budget mais ne sait pas quelle offre choisir?',
        siNon: '→ L\'Offre compare 3 options et recommande la meilleure'
      },
      {
        question: 'Avez-vous besoin de démontrer la valeur BZ?',
        siNon: '→ L\'Offre démontre clairement la valeur et le ROI'
      },
      {
        question: 'Le client veut-il voir la progression AS-IS/TARGET?',
        siNon: '→ L\'Offre visualise la progression avec chaque offre'
      },
      {
        question: 'Avez-vous besoin de fermer une vente?',
        siNon: '→ L\'Offre facilite la décision avec 3 options claires'
      },
    ],
    signaux: [
      'Client prospect demande proposition',
      'Budget client identifié',
      'Besoin de démontrer la valeur',
      'Comparaison d\'offres nécessaire',
      'Fermeture de vente',
      'Présentation commerciale'
    ],
    timing: [
      { timing: 'Lors d\'une prospection', raison: 'Pour présenter les offres BZ adaptées au budget' },
      { timing: 'Lors d\'un appel d\'offres', raison: 'Pour soumettre une proposition structurée' },
      { timing: 'Lors d\'une présentation commerciale', raison: 'Pour démontrer la valeur BZ' },
      { timing: 'Lors d\'une fermeture de vente', raison: 'Pour faciliter la décision avec 3 options' },
    ],
  },
  valeur: {
    heroTitle: "Comment expliquer la valeur au client?",
    heroDescription: "L'Offre de Service BZ n'est pas juste une proposition, c'est un <strong>outil de vente</strong> qui transforme les besoins en solutions adaptées.",
    benefices: [
      {
        benefice: '3 options adaptées',
        detail: 'Essentielle, Recommandée, Optimale selon le budget, facilitant la décision',
        icon: Star,
        color: 'indigo'
      },
      {
        benefice: 'Visualisation claire',
        detail: 'Spider charts AS-IS/TARGET montrant la progression avec chaque offre',
        icon: Target,
        color: 'blue'
      },
      {
        benefice: 'Valeur démontrée',
        detail: 'ROI, livrables tangibles et bénéfices clairs pour chaque offre',
        icon: TrendingUp,
        color: 'emerald'
      },
      {
        benefice: 'Comparaison facilitée',
        detail: 'Tableau comparatif des 3 offres, facilitant la décision',
        icon: BarChart,
        color: 'purple'
      },
      {
        benefice: 'Recommandation claire',
        detail: 'Offre Standard recommandée avec justification',
        icon: CheckCircle2,
        color: 'orange'
      },
      {
        benefice: 'Proposition professionnelle',
        detail: 'Document commercial de qualité, crédible pour la direction',
        icon: FileCheck,
        color: 'pink'
      }
    ],
    roi: {
      title: 'Retour sur investissement (ROI)',
      content: 'Une Offre de Service BZ bien structurée peut générer un ROI de 200-500% en:',
      points: [
        { label: 'Taux de conversion', value: 'Proposition claire = augmentation conversion de 30-50%' },
        { label: 'Valeur perçue', value: 'Démonstration valeur = prix moyen augmenté de 15-25%' },
        { label: 'Décision facilitée', value: '3 options claires = réduction délai décision de 40-60%' },
        { label: 'Fermeture de vente', value: 'Proposition professionnelle = fermeture facilitée' }
      ],
      cout: 'Coût d\'une Offre de Service BZ PRIsME: 3-8K$ (selon niveau)',
      valeur: 'Valeur générée: 50-200K$+ en ventes additionnelles'
    },
    arguments: [
      {
        persona: 'Pour le Client Prospect',
        arguments: [
          '3 options adaptées au budget',
          'Visualisation claire de la progression',
          'Valeur et ROI démontrés',
          'Décision facilitée'
        ]
      },
      {
        persona: 'Pour l\'Équipe Ventes BZ',
        arguments: [
          'Outil de présentation professionnel',
          'Adaptation rapide au budget client',
          'Démonstration claire de la valeur',
          'Fermeture de vente facilitée'
        ]
      },
    ],
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité",
    heroDescription: "PRIsME offre <strong>5 niveaux</strong> d'Offre de Service BZ, du résumé exécutif à l'offre complète avec analyses détaillées. Chaque niveau s'adapte à un besoin spécifique.",
    comparisonTable: [
      { critere: 'Pages (approximatif)', values: ['4-6', '8-12', '15-25', '30-45', '50-70'] },
      { critere: 'Public cible', values: ['Client (résumé)', 'Client + Direction', 'Direction + Comité', 'Gouvernance complète', 'CA'] },
      { critere: 'Détail des offres', values: ['Basique', 'Moyen', 'Détaillé', 'Très détaillé', 'Complet avec analyses'] },
      { critere: 'Spider charts', values: ['1 global', '1 par offre', '1 détaillé par offre', 'Très détaillés', 'Complets avec scénarios'] },
      { critere: 'Livrables', values: ['Liste simple', 'Basiques', 'Détaillés', 'Très détaillés', 'Complets avec KPIs'] },
      { critere: 'ROI', values: ['Mentionné', 'Basique', 'Détaillé', 'Très détaillé', 'Complet avec scénarios'] },
      { critere: 'Prix indicatif', values: ['3-5K$', '5-8K$', '8-12K$', '12-18K$', '18-25K$'] },
    ],
    niveauDetails: [
      {
        inclus: [
          'Résumé exécutif (1-2 pages)',
          '3 offres basiques',
          'Spider chart global',
          'Livrables simples',
          'ROI mentionné',
          'Langage simple, accessible'
        ],
        quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, client simple.'
      },
      {
        inclus: [
          'Résumé exécutif + sections standardisées',
          '3 offres détaillées',
          'Spider chart par offre',
          'Livrables basiques',
          'ROI basique',
          'Comparaison simple'
        ],
        quandUtiliser: 'Pour PME standard, besoin de structuration, première approche formelle de la proposition.'
      },
      {
        inclus: [
          'Tout le niveau 2, PLUS:',
          '3 offres très détaillées',
          'Spider charts détaillés',
          'Livrables détaillés',
          'ROI détaillé',
          'Comparaison détaillée',
          'Intégration Cadre BZ'
        ],
        quandUtiliser: 'Pour PME en croissance, besoin complet, alimenter décision, direction formelle.'
      },
      {
        inclus: [
          'Tout le niveau 3, PLUS:',
          '3 offres exhaustives',
          'Spider charts très détaillés',
          'Livrables très détaillés avec KPIs',
          'ROI très détaillé',
          'Comparaison exhaustive',
          'Ton très professionnel, direction-ready'
        ],
        quandUtiliser: 'Pour entreprises matures, gouvernance importante, décision importante, comité formel.'
      },
      {
        inclus: [
          'Tout le niveau 4, PLUS:',
          '3 offres complètes avec analyses',
          'Spider charts complets avec scénarios',
          'Livrables complets avec KPIs',
          'ROI complet avec scénarios',
          'Comparaison exhaustive',
          'Document CA-ready',
          'Ton exécutif, très visuel'
        ],
        quandUtiliser: 'Pour grandes entreprises, décision majeure, présentation CA, gouvernance complète.'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Budget mensuel estimé: 3000$ CAD (client moyenne taille 15-25 utilisateurs).
Besoins identifiés: Sécurité, conformité, modernisation, gouvernance, support proactif.`);

// POLITIQUE_IA
export const PolitiqueIAModal = createDocumentModal('POLITIQUE_IA', {
  esprit: {
    heroTitle: "L'Esprit de la Politique d'Utilisation de l'Intelligence Artificielle",
    heroDescription: "La Politique IA est le <strong>document de gouvernance</strong> qui encadre l'utilisation responsable et sécurisée de l'intelligence artificielle. C'est l'outil qui transforme les risques IA en opportunités maîtrisées.",
    pourquoi: {
      probleme: "Sans politique IA claire, les utilisateurs ne savent pas <strong>comment utiliser</strong> l'IA de manière responsable, les risques augmentent (fuites de données, biais, erreurs), la conformité est compromise.",
      solution: "La Politique IA établit des <strong>règles claires</strong> pour l'utilisation de l'IA, couvrant la gouvernance, la gestion des risques, l'éthique et la conformité, garantissant une utilisation responsable et sécurisée."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si l'IA est un <strong>outil puissant</strong>, la Politique IA est le <strong>manuel d'utilisation</strong> qui définit:",
      points: [
        "Quels outils IA peuvent être utilisés (catalogue autorisé)",
        "Comment les utiliser de manière sécurisée (restrictions et contrôles)",
        "Quelles données peuvent être partagées (protection et conformité)",
        "Comment gérer les risques (biais, erreurs, conformité)",
        "Quelles sont les responsabilités (éthique et imputabilité)"
      ],
      conclusion: "La Politique IA, c'est exactement ça: le <strong>guide complet</strong> qui garantit que tous les utilisateurs savent comment utiliser l'IA de manière responsable et conforme."
    },
    piliers: [
      { icon: Building2, title: '1. Gouvernance IA', desc: 'Structure de gouvernance et responsabilités', color: 'purple' },
      { icon: AlertTriangle, title: '2. Cartographie des Risques', desc: 'Identification et évaluation des risques IA', color: 'red' },
      { icon: Shield, title: '3. Mesure & Contrôles', desc: 'Contrôles techniques et organisationnels', color: 'blue' },
      { icon: CheckCircle2, title: '4. Éthique & Responsabilités', desc: 'Principes éthiques et responsabilités utilisateurs', color: 'green' },
      { icon: FileCheck, title: '5. Conformité & Formation', desc: 'Loi 25, formation, amélioration continue', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Alignée sur NIST AI RMF', desc: 'Conforme au cadre de gestion des risques IA du NIST' },
      { title: 'Couvre tous les aspects', desc: 'Gouvernance, risques, contrôles, éthique, conformité' },
      { title: 'Adaptée au contexte', desc: 'Personnalisée selon les cas d\'usage et risques identifiés' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la politique complète (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Politique IA?",
    heroDescription: "La Politique s'adresse principalement à <strong>tous les utilisateurs</strong> d'IA, à la <strong>direction</strong> et à la <strong>gouvernance</strong> qui doivent comprendre et appliquer les règles d'utilisation responsable de l'IA.",
    profils: [
      {
        title: 'Tous les Utilisateurs d\'IA',
        icon: UsersIcon,
        color: 'purple',
        scenarios: [
          'Comprendre les outils IA autorisés',
          'Savoir comment utiliser l\'IA de manière responsable',
          'Connaître les restrictions et obligations',
          'Respecter la conformité et l\'éthique'
        ],
        niveau: 'Niveau 2-3 recommandé'
      },
      {
        title: 'Direction / Gouvernance',
        icon: Building2,
        color: 'indigo',
        scenarios: [
          'Comprendre la gouvernance IA',
          'Valider les risques et contrôles',
          'Approuver les cas d\'usage',
          'Décider des investissements IA'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Équipe TI / Sécurité',
        icon: Shield,
        color: 'blue',
        scenarios: [
          'Implémenter les contrôles techniques',
          'Monitorer l\'utilisation IA',
          'Gérer les risques IA',
          'Assurer la conformité'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Politique IA?",
    heroDescription: "La Politique doit être livrée <strong>avant ou au début</strong> de l'adoption de l'IA dans l'organisation pour établir les règles dès le départ.",
    moments: [
      {
        title: 'Avant adoption de l\'IA',
        icon: Rocket,
        color: 'green',
        description: 'Établir les règles avant que les utilisateurs commencent à utiliser l\'IA'
      },
      {
        title: 'Lors de projets pilotes IA',
        icon: Zap,
        color: 'yellow',
        description: 'Encadrer les projets pilotes avec des règles claires'
      },
      {
        title: 'Pour conformité réglementaire',
        icon: FileCheck,
        color: 'blue',
        description: 'Répondre aux obligations Loi 25 et autres exigences réglementaires'
      },
      {
        title: 'Après incidents IA',
        icon: AlertTriangle,
        color: 'red',
        description: 'Renforcer les règles après un incident ou une violation'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Politique IA",
    heroDescription: "La Politique transforme l'adoption de l'IA en <strong>opportunité maîtrisée</strong>, garantissant la conformité, la sécurité et l'utilisation responsable.",
    avantages: [
      {
        title: 'Gouvernance structurée',
        desc: 'Cadre clair pour l\'adoption et l\'utilisation de l\'IA',
        icon: Building2,
        color: 'purple'
      },
      {
        title: 'Risques maîtrisés',
        desc: 'Identification, mesure et gestion des risques IA',
        icon: Shield,
        color: 'blue'
      },
      {
        title: 'Conformité garantie',
        desc: 'Respect des obligations Loi 25 et réglementaires',
        icon: FileCheck,
        color: 'green'
      },
      {
        title: 'Utilisation responsable',
        desc: 'Promotion d\'une utilisation éthique et sécurisée',
        icon: CheckCircle2,
        color: 'orange'
      },
    ],
    roi: {
      title: 'ROI de la Politique IA',
      description: 'La Politique IA génère de la valeur en:',
      points: [
        'Réduisant les risques de fuites de données et violations (évite les amendes et dommages)',
        'Prévenant les biais et erreurs IA (maintient la qualité et la réputation)',
        'Démontrant la conformité (évite les sanctions réglementaires)',
        'Optimisant l\'adoption de l\'IA (permet une utilisation efficace et sécurisée)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Politique IA",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>politique exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Politique Synthèse',
        pages: '3-5 pages',
        contenu: 'Règles essentielles, outils autorisés, restrictions principales',
        pour: 'Utilisateurs (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Politique Structurée',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, cartographie des risques, contrôles de base',
        pour: 'Direction (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Politique Complète',
        pages: '10-15 pages',
        contenu: 'Analyse approfondie des risques, procédures détaillées, plan de formation',
        pour: 'Gouvernance (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Politique Gérée',
        pages: '15-20 pages',
        contenu: 'Gouvernance IA structurée, métriques de mesure, conformité Loi 25 détaillée',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Politique Optimisée',
        pages: '20-25 pages',
        contenu: 'Workflows complets, KPIs de conformité, plan d\'amélioration continue structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés, croissance 10%/an.
Objectifs d'affaires: améliorer la productivité, automatiser les processus, mieux exploiter les données.
Maturité IA: projets pilotes ChatGPT et Copilot, pas de politique claire.
Enjeux IA: risques de fuites de données, biais algorithmiques, conformité Loi 25, utilisation non contrôlée.
Besoins: Politique claire, gouvernance IA, formation, gestion des risques, conformité.`);

// INCIDENT_RESPONSE_POLICY
export const PolitiqueGestionIncidentsModal = createDocumentModal('INCIDENT_RESPONSE_POLICY', {
  esprit: {
    heroTitle: "L'Esprit de la Politique de Gestion des Incidents",
    heroDescription: "La Politique de Gestion des Incidents est le <strong>document maître de gouvernance</strong> qui définit les objectifs, la portée, la gouvernance, les enjeux et les responsabilités pour la gestion des incidents TI. C'est l'outil qui transforme le chaos en processus structuré.",
    pourquoi: {
      probleme: "Sans politique de gestion des incidents claire, les incidents ne sont pas <strong>gérés efficacement</strong>, les délais de résolution augmentent, la communication est inadéquate, la conformité est compromise.",
      solution: "La Politique de Gestion des Incidents établit un <strong>cadre de gouvernance</strong> complet pour la gestion des incidents, garantissant une résolution rapide et efficace."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident TI est un <strong>feu</strong>, la Politique de Gestion des Incidents est le <strong>plan d'intervention d'urgence</strong> qui définit:",
      points: [
        "Qui intervient et quand (rôles et responsabilités)",
        "Comment intervenir (processus et procédures)",
        "Quand escalader (critères et délais)",
        "Comment communiquer (canaux et templates)",
        "Comment améliorer (révision et amélioration continue)"
      ],
      conclusion: "La Politique de Gestion des Incidents, c'est exactement ça: le <strong>plan d'intervention</strong> qui garantit que chaque incident est géré de manière efficace et structurée."
    },
    piliers: [
      { icon: Building2, title: '1. Gouvernance & Structure', desc: 'Structure de gouvernance et responsabilités', color: 'blue' },
      { icon: AlertTriangle, title: '2. Enjeux & Risques', desc: 'Enjeux business et risques identifiés', color: 'red' },
      { icon: FileCheck, title: '3. Processus & Procédures', desc: 'Processus ITIL 4 et procédures détaillées', color: 'green' },
      { icon: MessageSquare, title: '4. Communication', desc: 'Communication lors d\'incidents', color: 'orange' },
      { icon: CheckCircle2, title: '5. Formation & Amélioration', desc: 'Formation et amélioration continue', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO/NIST', desc: 'Alignée sur les meilleurs cadres de gestion d\'incidents' },
      { title: 'Document maître de gouvernance', desc: 'Définit objectifs, portée, gouvernance, enjeux, responsabilités' },
      { title: 'Processus structuré', desc: 'Processus ITIL 4 complet pour gestion efficace' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la politique exhaustive (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Politique de Gestion des Incidents?",
    heroDescription: "La Politique s'adresse principalement à la <strong>direction</strong>, à la <strong>gouvernance</strong> et à l'<strong>équipe TI</strong> qui doivent comprendre et appliquer le cadre de gestion des incidents.",
    profils: [
      {
        title: 'Direction / Gouvernance',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Comprendre la gouvernance des incidents',
          'Valider les objectifs et enjeux',
          'Approuver les processus',
          'Décider des investissements'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Équipe TI / Gestion d\'Incidents',
        icon: Shield,
        color: 'green',
        scenarios: [
          'Implémenter les processus',
          'Gérer les incidents selon la politique',
          'Communiquer efficacement',
          'Améliorer continuellement'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Politique de Gestion des Incidents?",
    heroDescription: "La Politique doit être livrée <strong>avant</strong> la mise en place du processus de gestion des incidents pour établir le cadre de gouvernance.",
    moments: [
      {
        title: 'Mise en place processus incidents',
        icon: Rocket,
        color: 'green',
        description: 'Établir le cadre de gouvernance avant la mise en place'
      },
      {
        title: 'Amélioration processus existant',
        icon: Zap,
        color: 'yellow',
        description: 'Structurer et améliorer un processus existant'
      },
      {
        title: 'Conformité réglementaire',
        icon: FileCheck,
        color: 'blue',
        description: 'Répondre aux obligations réglementaires'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Politique de Gestion des Incidents",
    heroDescription: "La Politique transforme la gestion des incidents en <strong>processus structuré</strong>, garantissant une résolution rapide et efficace.",
    avantages: [
      {
        title: 'Gouvernance structurée',
        desc: 'Cadre clair pour la gestion des incidents',
        icon: Building2,
        color: 'blue'
      },
      {
        title: 'Résolution rapide',
        desc: 'Processus efficace pour réduire les délais',
        icon: Clock,
        color: 'green'
      },
      {
        title: 'Communication efficace',
        desc: 'Canaux et templates pour communication claire',
        icon: MessageSquare,
        color: 'orange'
      },
      {
        title: 'Conformité garantie',
        desc: 'Respect des obligations réglementaires',
        icon: FileCheck,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI de la Politique',
      description: 'La Politique génère de la valeur en:',
      points: [
        'Réduisant les délais de résolution (améliore la productivité)',
        'Améliorant la communication (maintient la confiance)',
        'Garantissant la conformité (évite les sanctions)',
        'Optimisant les ressources (allocation efficace)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Politique",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>politique exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Politique Synthèse',
        pages: '3-5 pages',
        contenu: 'Objectifs, portée, gouvernance, responsabilités principales',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Politique Structurée',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, gouvernance détaillée, processus de base',
        pour: 'Gouvernance (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Politique Complète',
        pages: '10-15 pages',
        contenu: 'Procédures détaillées, matrices de responsabilités, workflows complets',
        pour: 'Équipe TI (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Politique Gérée',
        pages: '15-20 pages',
        contenu: 'Métriques de performance, KPIs, amélioration continue, conformité détaillée',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Politique Optimisée',
        pages: '20-25 pages',
        contenu: 'Scénarios d\'application, workflows détaillés, KPIs de conformité, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes.
Enjeux TI: incidents fréquents, délais de résolution longs, communication inadéquate, conformité.
Besoins: Politique claire, gouvernance structurée, processus efficace, formation.`);

// INFORMATION_SECURITY_POLICY
export const PolitiqueSecuriteInformationModal = createDocumentModal('INFORMATION_SECURITY_POLICY', {
  esprit: {
    heroTitle: "L'Esprit de la Politique de Sécurité de l'Information",
    heroDescription: "La Politique de Sécurité de l'Information est le <strong>document maître de référence</strong> pour la sécurité de l'information. C'est le document que le plan d'incident et autres politiques doivent respecter. C'est l'outil qui transforme la sécurité en culture organisationnelle.",
    pourquoi: {
      probleme: "Sans politique de sécurité de l'information claire, les données ne sont pas <strong>protégées efficacement</strong>, les risques augmentent, la conformité est compromise, la réputation est menacée.",
      solution: "La Politique de Sécurité de l'Information établit un <strong>cadre de référence</strong> complet pour la protection des actifs d'information, garantissant la confidentialité, l'intégrité et la disponibilité."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si les informations sont des <strong>biens précieux</strong>, la Politique de Sécurité de l'Information est le <strong>système de sécurité</strong> qui définit:",
      points: [
        "Quels biens protéger (classification des données)",
        "Comment les protéger (contrôles de sécurité)",
        "Qui est responsable (gouvernance et responsabilités)",
        "Quand réviser (amélioration continue)",
        "Comment garantir la conformité (audits et contrôles)"
      ],
      conclusion: "La Politique de Sécurité de l'Information, c'est exactement ça: le <strong>système de sécurité</strong> qui garantit que toutes les informations sont protégées selon leur valeur."
    },
    piliers: [
      { icon: Shield, title: '1. Principes de Sécurité', desc: 'Confidentialité, Intégrité, Disponibilité (CIA)', color: 'blue' },
      { icon: Building2, title: '2. Gouvernance', desc: 'Structure de gouvernance et responsabilités', color: 'purple' },
      { icon: FileCheck, title: '3. Contrôles de Sécurité', desc: 'Contrôles techniques et organisationnels', color: 'green' },
      { icon: Layers, title: '4. Classification des Données', desc: 'Système de classification par sensibilité', color: 'orange' },
      { icon: CheckCircle2, title: '5. Conformité & Formation', desc: 'Conformité réglementaire et formation', color: 'red' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ISO 27001/ITIL/COBIT/NIST', desc: 'Alignée sur les meilleurs cadres de sécurité' },
      { title: 'Document maître de référence', desc: 'Document que toutes les autres politiques doivent respecter' },
      { title: 'Protection complète', desc: 'Couvre tous les aspects de la sécurité de l\'information' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la politique exhaustive (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Politique de Sécurité de l'Information?",
    heroDescription: "La Politique s'adresse principalement à <strong>tous les utilisateurs</strong>, à la <strong>direction</strong> et à l'<strong>équipe TI</strong> qui doivent comprendre et appliquer les principes de sécurité.",
    profils: [
      {
        title: 'Tous les Utilisateurs',
        icon: UsersIcon,
        color: 'blue',
        scenarios: [
          'Comprendre les principes de sécurité',
          'Savoir comment protéger les données',
          'Connaître les responsabilités',
          'Respecter la classification des données'
        ],
        niveau: 'Niveau 2-3 recommandé'
      },
      {
        title: 'Direction / Gouvernance',
        icon: Building2,
        color: 'purple',
        scenarios: [
          'Comprendre la gouvernance sécurité',
          'Valider les contrôles',
          'Approuver les investissements',
          'Décider des priorités'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Équipe TI / Sécurité',
        icon: Shield,
        color: 'green',
        scenarios: [
          'Implémenter les contrôles',
          'Gérer la classification',
          'Assurer la conformité',
          'Former les utilisateurs'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Politique de Sécurité de l'Information?",
    heroDescription: "La Politique doit être livrée <strong>en premier</strong> car c'est le document maître de référence que toutes les autres politiques doivent respecter.",
    moments: [
      {
        title: 'Mise en place sécurité',
        icon: Rocket,
        color: 'green',
        description: 'Établir le cadre de référence avant les autres politiques'
      },
      {
        title: 'Conformité ISO 27001',
        icon: FileCheck,
        color: 'blue',
        description: 'Répondre aux exigences ISO 27001'
      },
      {
        title: 'Amélioration sécurité existante',
        icon: Zap,
        color: 'yellow',
        description: 'Structurer et améliorer la sécurité existante'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Politique de Sécurité de l'Information",
    heroDescription: "La Politique transforme la sécurité en <strong>culture organisationnelle</strong>, garantissant la protection des actifs d'information.",
    avantages: [
      {
        title: 'Protection complète',
        desc: 'Cadre de référence pour protéger tous les actifs',
        icon: Shield,
        color: 'blue'
      },
      {
        title: 'Conformité garantie',
        desc: 'Respect des obligations réglementaires',
        icon: FileCheck,
        color: 'green'
      },
      {
        title: 'Classification claire',
        desc: 'Système de classification pour gérer par sensibilité',
        icon: Layers,
        color: 'orange'
      },
      {
        title: 'Culture de sécurité',
        desc: 'Promotion d\'une culture de sécurité',
        icon: CheckCircle2,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI de la Politique',
      description: 'La Politique génère de la valeur en:',
      points: [
        'Réduisant les risques de sécurité (évite les incidents)',
        'Garantissant la conformité (évite les sanctions)',
        'Protégeant la réputation (maintient la confiance)',
        'Optimisant les investissements (allocation efficace)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Politique",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>politique exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Politique Synthèse',
        pages: '3-5 pages',
        contenu: 'Objectifs, portée, principes CIA, responsabilités principales',
        pour: 'Utilisateurs (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Politique Structurée',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, principes détaillés, contrôles de base',
        pour: 'Direction (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Politique Complète',
        pages: '10-15 pages',
        contenu: 'Contrôles complets, classification détaillée, gestion des risques',
        pour: 'Gouvernance (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Politique Gérée',
        pages: '15-20 pages',
        contenu: 'Métriques de sécurité, KPIs, processus d\'audit, conformité détaillée',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Politique Optimisée',
        pages: '20-25 pages',
        contenu: 'Scénarios d\'application, contrôles détaillés, KPIs de conformité, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes.
Enjeux TI: sécurité faible, conformité Loi 25, protection des données clients.
Besoins: Politique claire, gouvernance structurée, classification des données, formation.`);

// INCIDENT_CLASSIFICATION_POLICY
export const PolitiqueClassificationIncidentsModal = createDocumentModal('INCIDENT_CLASSIFICATION_POLICY', {
  esprit: {
    heroTitle: "L'Esprit de la Politique de Classification des Incidents",
    heroDescription: "La Politique de Classification des Incidents est le <strong>document de référence</strong> qui définit la sévérité, l'impact, l'urgence et les niveaux de priorité (P1-P4 ITIL / CAT 1-5 NIST). C'est l'outil qui transforme la confusion en classification cohérente.",
    pourquoi: {
      probleme: "Sans politique de classification claire, les incidents ne sont pas <strong>classifiés de manière cohérente</strong>, les priorités sont incorrectes, les délais ne sont pas respectés, les ressources sont mal allouées.",
      solution: "La Politique de Classification établit des <strong>critères clairs</strong> pour classifier les incidents, garantissant une priorisation cohérente et efficace."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident TI est un <strong>patient aux urgences</strong>, la Politique de Classification est le <strong>système de triage</strong> qui définit:",
      points: [
        "Quels patients sont critiques (P1/CAT 1)",
        "Quels patients sont majeurs (P2/CAT 2)",
        "Quels patients sont mineurs (P3/CAT 3)",
        "Quels patients sont des questions (P4/CAT 5)",
        "Quels délais de traitement pour chaque niveau"
      ],
      conclusion: "La Politique de Classification, c'est exactement ça: le <strong>système de triage</strong> qui garantit que chaque incident est traité selon sa priorité réelle."
    },
    piliers: [
      { icon: AlertTriangle, title: '1. Système de Classification', desc: 'ITIL P1-P4 ou NIST CAT 1-5', color: 'red' },
      { icon: Target, title: '2. Facteurs de Classification', desc: 'Impact, urgence, sévérité, utilisateurs', color: 'orange' },
      { icon: Clock, title: '3. Délais Cibles', desc: 'Délais de résolution par niveau', color: 'yellow' },
      { icon: FileCheck, title: '4. Matrice de Décision', desc: 'Matrice pour classification rapide', color: 'green' },
      { icon: CheckCircle2, title: '5. Processus & Formation', desc: 'Processus de classification et formation', color: 'blue' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO/NIST', desc: 'Alignée sur les meilleurs cadres de classification' },
      { title: 'Classification cohérente', desc: 'Critères clairs pour classification rapide et précise' },
      { title: 'Délais cibles définis', desc: 'Délais de résolution par niveau de priorité' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la politique exhaustive (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Politique de Classification des Incidents?",
    heroDescription: "La Politique s'adresse principalement à l'<strong>équipe TI</strong> et aux <strong>gestionnaires d'incidents</strong> qui doivent classifier les incidents de manière cohérente.",
    profils: [
      {
        title: 'Équipe TI / Support',
        icon: Shield,
        color: 'green',
        scenarios: [
          'Classifier les incidents correctement',
          'Respecter les délais cibles',
          'Utiliser la matrice de décision',
          'Appliquer les critères'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Valider les classifications',
          'Gérer les reclassifications',
          'Superviser le processus',
          'Améliorer la cohérence'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Politique de Classification des Incidents?",
    heroDescription: "La Politique doit être livrée <strong>en même temps</strong> que la Politique de Gestion des Incidents pour établir les critères de classification.",
    moments: [
      {
        title: 'Mise en place processus incidents',
        icon: Rocket,
        color: 'green',
        description: 'Établir les critères de classification avec le processus'
      },
      {
        title: 'Amélioration classification existante',
        icon: Zap,
        color: 'yellow',
        description: 'Standardiser et améliorer la classification existante'
      },
      {
        title: 'Problèmes de cohérence',
        icon: AlertTriangle,
        color: 'red',
        description: 'Résoudre les problèmes de classification incohérente'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Politique de Classification",
    heroDescription: "La Politique transforme la classification en <strong>processus cohérent</strong>, garantissant une priorisation efficace.",
    avantages: [
      {
        title: 'Classification cohérente',
        desc: 'Critères clairs pour classification rapide',
        icon: Target,
        color: 'green'
      },
      {
        title: 'Priorisation efficace',
        desc: 'Ressources allouées selon la priorité réelle',
        icon: AlertTriangle,
        color: 'orange'
      },
      {
        title: 'Délais respectés',
        desc: 'Délais cibles définis et mesurables',
        icon: Clock,
        color: 'blue'
      },
      {
        title: 'Amélioration continue',
        desc: 'Processus de révision pour améliorer la cohérence',
        icon: CheckCircle2,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI de la Politique',
      description: 'La Politique génère de la valeur en:',
      points: [
        'Réduisant les erreurs de classification (améliore l\'efficacité)',
        'Respectant les délais cibles (améliore la satisfaction)',
        'Optimisant les ressources (allocation efficace)',
        'Améliorant la cohérence (réduit les conflits)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Politique",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>politique exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Politique Synthèse',
        pages: '3-5 pages',
        contenu: 'Système de classification, niveaux de base, critères principaux',
        pour: 'Équipe TI (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Politique Structurée',
        pages: '6-10 pages',
        contenu: 'Définitions détaillées des niveaux, critères complets, délais cibles',
        pour: 'Support (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Politique Complète',
        pages: '10-15 pages',
        contenu: 'Matrice de décision détaillée, exemples concrets, processus de reclassification',
        pour: 'Gestionnaires (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Politique Gérée',
        pages: '15-20 pages',
        contenu: 'Métriques de classification, KPIs, analyse comparative ITIL vs NIST',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Politique Optimisée',
        pages: '20-25 pages',
        contenu: 'Scénarios d\'application détaillés, matrices avancées, KPIs de conformité',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: classification incohérente, délais non respectés, priorités incorrectes.
Besoins: Politique claire, critères définis, matrice de décision, formation.`);

// INCIDENT_ESCALATION_POLICY
export const PolitiqueEscaladeIncidentsModal = createDocumentModal('INCIDENT_ESCALATION_POLICY', {
  esprit: {
    heroTitle: "L'Esprit de la Politique d'Escalade des Incidents",
    heroDescription: "La Politique d'Escalade des Incidents est le <strong>document de référence</strong> qui définit les responsables, délais d'escalade, critères et processus de communication pour l'escalade des incidents. C'est l'outil qui transforme l'escalade en processus structuré.",
    pourquoi: {
      probleme: "Sans politique d'escalade claire, les incidents ne sont pas <strong>escaladés efficacement</strong>, les délais sont dépassés, les responsables ne sont pas identifiés, la communication est inadéquate.",
      solution: "La Politique d'Escalade établit un <strong>processus structuré</strong> pour l'escalade des incidents, garantissant une résolution rapide et efficace."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident TI est un <strong>problème à résoudre</strong>, la Politique d'Escalade est le <strong>système d'escalade hiérarchique</strong> qui définit:",
      points: [
        "Qui intervient au niveau 1 (première ligne)",
        "Quand escalader au niveau 2 (deuxième ligne)",
        "Quand escalader au niveau 3 (experts)",
        "Quand escalader au niveau 4 (direction)",
        "Comment communiquer à chaque niveau"
      ],
      conclusion: "La Politique d'Escalade, c'est exactement ça: le <strong>système d'escalade</strong> qui garantit que chaque incident est escaladé au bon moment et à la bonne personne."
    },
    piliers: [
      { icon: UsersIcon, title: '1. Structure d\'Escalade', desc: 'Niveaux d\'escalade et responsables', color: 'blue' },
      { icon: Clock, title: '2. Délais d\'Escalade', desc: 'Délais avant escalade par niveau', color: 'orange' },
      { icon: AlertTriangle, title: '3. Critères d\'Escalade', desc: 'Critères et triggers d\'escalade', color: 'red' },
      { icon: MessageSquare, title: '4. Communication', desc: 'Canaux et templates de communication', color: 'green' },
      { icon: FileCheck, title: '5. Processus & Documentation', desc: 'Processus ITIL 4 et documentation', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO/NIST', desc: 'Alignée sur les meilleurs cadres d\'escalade' },
      { title: 'Processus structuré', desc: 'Processus ITIL 4 complet pour escalade efficace' },
      { title: 'Communication claire', desc: 'Canaux et templates pour communication efficace' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la politique exhaustive (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Politique d'Escalade des Incidents?",
    heroDescription: "La Politique s'adresse principalement à l'<strong>équipe TI</strong> et aux <strong>gestionnaires d'incidents</strong> qui doivent escalader les incidents de manière efficace.",
    profils: [
      {
        title: 'Équipe TI / Support',
        icon: Shield,
        color: 'green',
        scenarios: [
          'Escalader les incidents au bon moment',
          'Identifier les responsables',
          'Respecter les délais',
          'Communiquer efficacement'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Superviser l\'escalade',
          'Valider les escalades',
          'Gérer les workflows',
          'Améliorer le processus'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Politique d'Escalade des Incidents?",
    heroDescription: "La Politique doit être livrée <strong>en même temps</strong> que la Politique de Gestion des Incidents pour établir les processus d'escalade.",
    moments: [
      {
        title: 'Mise en place processus incidents',
        icon: Rocket,
        color: 'green',
        description: 'Établir les processus d\'escalade avec le processus principal'
      },
      {
        title: 'Problèmes d\'escalade',
        icon: AlertTriangle,
        color: 'red',
        description: 'Résoudre les problèmes d\'escalade inefficace'
      },
      {
        title: 'Amélioration processus existant',
        icon: Zap,
        color: 'yellow',
        description: 'Structurer et améliorer l\'escalade existante'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Politique d'Escalade",
    heroDescription: "La Politique transforme l'escalade en <strong>processus structuré</strong>, garantissant une résolution rapide et efficace.",
    avantages: [
      {
        title: 'Escalade efficace',
        desc: 'Processus structuré pour escalade rapide',
        icon: Rocket,
        color: 'green'
      },
      {
        title: 'Délais respectés',
        desc: 'Délais d\'escalade définis et mesurables',
        icon: Clock,
        color: 'blue'
      },
      {
        title: 'Communication claire',
        desc: 'Canaux et templates pour communication efficace',
        icon: MessageSquare,
        color: 'orange'
      },
      {
        title: 'Responsabilités claires',
        desc: 'Responsables identifiés à chaque niveau',
        icon: UsersIcon,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI de la Politique',
      description: 'La Politique génère de la valeur en:',
      points: [
        'Réduisant les délais d\'escalade (améliore la résolution)',
        'Améliorant la communication (maintient la confiance)',
        'Optimisant les ressources (allocation efficace)',
        'Garantissant la responsabilité (imputabilité claire)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Politique",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>politique exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Politique Synthèse',
        pages: '3-5 pages',
        contenu: 'Structure d\'escalade, responsables par niveau, délais principaux',
        pour: 'Équipe TI (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Politique Structurée',
        pages: '6-10 pages',
        contenu: 'Structure détaillée, responsables, délais complets, critères d\'escalade',
        pour: 'Support (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Politique Complète',
        pages: '10-15 pages',
        contenu: 'Workflows d\'escalade complets, matrices de décision, processus de communication',
        pour: 'Gestionnaires (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Politique Gérée',
        pages: '15-20 pages',
        contenu: 'Métriques d\'escalade, KPIs, analyse des escalades, amélioration continue',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Politique Optimisée',
        pages: '20-25 pages',
        contenu: 'Scénarios d\'escalade détaillés, workflows avancés, KPIs de performance',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: escalade inefficace, délais dépassés, responsables non identifiés, communication inadéquate.
Besoins: Politique claire, processus structuré, responsables identifiés, communication efficace.`);

// INCIDENT_RESPONSE_PLAN
export const PlanInterventionIncidentModal = createDocumentModal('INCIDENT_RESPONSE_PLAN', {
  esprit: {
    heroTitle: "L'Esprit du Plan d'Intervention en cas d'Incident (IRP)",
    heroDescription: "Le Plan d'Intervention est le <strong>guide opérationnel</strong> qui définit les procédures, rôles, outils et workflows pour répondre efficacement aux incidents de sécurité. C'est l'outil qui transforme le chaos en réponse structurée.",
    pourquoi: {
      probleme: "Sans plan d'intervention clair, les incidents ne sont pas <strong>gérés efficacement</strong>, les délais de résolution augmentent, les mesures de confinement sont inadéquates, les données sont compromises.",
      solution: "Le Plan d'Intervention établit des <strong>procédures structurées</strong> pour la détection, analyse, confinement, éradication et restauration, garantissant une réponse rapide et efficace."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident de sécurité est un <strong>feu</strong>, le Plan d'Intervention est le <strong>manuel d'intervention des pompiers</strong> qui définit:",
      points: [
        "Comment détecter le feu (sources de détection)",
        "Comment analyser la situation (processus d'analyse)",
        "Comment confiner le feu (mesures de confinement)",
        "Comment éteindre le feu (mesures d'éradication)",
        "Comment restaurer les dommages (processus de restauration)"
      ],
      conclusion: "Le Plan d'Intervention, c'est exactement ça: le <strong>manuel d'intervention</strong> qui garantit que chaque incident est géré de manière efficace et structurée."
    },
    piliers: [
      { icon: UsersIcon, title: '1. Équipe d\'Intervention', desc: 'Composition, rôles et contacts d\'urgence', color: 'blue' },
      { icon: Eye, title: '2. Détection & Analyse', desc: 'Sources de détection et processus d\'analyse', color: 'green' },
      { icon: Shield, title: '3. Confinement & Éradication', desc: 'Mesures de confinement et d\'éradication', color: 'red' },
      { icon: CheckCircle2, title: '4. Restauration & Post-Incident', desc: 'Processus de restauration et activités post-incident', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Conforme NIST CSF 2.0/ITIL/ISO/COBIT', desc: 'Aligné sur les meilleurs cadres de réponse aux incidents' },
      { title: 'Procédures structurées', desc: 'Workflows détaillés pour chaque phase de réponse' },
      { title: 'Prêt à utiliser', desc: 'Document opérationnel pour utilisation en situation d\'urgence' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au plan exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Plan d'Intervention?",
    heroDescription: "Le Plan s'adresse principalement à l'<strong>équipe d'intervention</strong> et aux <strong>gestionnaires d'incidents</strong> qui doivent répondre aux incidents de sécurité.",
    profils: [
      {
        title: 'Équipe d\'Intervention',
        icon: Shield,
        color: 'red',
        scenarios: [
          'Utiliser les procédures de détection',
          'Appliquer les mesures de confinement',
          'Exécuter les processus d\'éradication',
          'Effectuer la restauration'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Coordonner l\'équipe',
          'Superviser les workflows',
          'Valider les actions',
          'Gérer la communication'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer le Plan d'Intervention?",
    heroDescription: "Le Plan doit être livré <strong>avant</strong> tout incident pour que l'équipe soit préparée.",
    moments: [
      {
        title: 'Mise en place réponse incidents',
        icon: Rocket,
        color: 'green',
        description: 'Établir les procédures avant les incidents'
      },
      {
        title: 'Amélioration processus existant',
        icon: Zap,
        color: 'yellow',
        description: 'Structurer et améliorer les processus existants'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur du Plan d'Intervention",
    heroDescription: "Le Plan transforme la réponse aux incidents en <strong>processus structuré</strong>, garantissant une réponse rapide et efficace.",
    avantages: [
      {
        title: 'Réponse rapide',
        desc: 'Procédures claires pour réponse immédiate',
        icon: Clock,
        color: 'green'
      },
      {
        title: 'Confinement efficace',
        desc: 'Mesures de confinement pour limiter l\'impact',
        icon: Shield,
        color: 'red'
      },
      {
        title: 'Éradication complète',
        desc: 'Processus d\'éradication pour éliminer la menace',
        icon: CheckCircle2,
        color: 'blue'
      },
      {
        title: 'Restauration rapide',
        desc: 'Processus de restauration pour rétablir les services',
        icon: Rocket,
        color: 'orange'
      },
    ],
    roi: {
      title: 'ROI du Plan',
      description: 'Le Plan génère de la valeur en:',
      points: [
        'Réduisant les délais de résolution (améliore la productivité)',
        'Limitant l\'impact des incidents (réduit les dommages)',
        'Garantissant l\'éradication complète (prévient les récurrences)',
        'Optimisant la restauration (minimise les interruptions)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité du Plan",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>plan exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Plan Synthèse',
        pages: '3-5 pages',
        contenu: 'Équipe, détection, confinement, restauration de base',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Plan Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les procédures principales, workflows de base',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Plan Complet',
        pages: '10-15 pages',
        contenu: 'Procédures détaillées, workflows complets, outils',
        pour: 'Équipe d\'intervention (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Plan Géré',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, amélioration continue, scénarios',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Plan Optimisé',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, workflows avancés, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: réponse aux incidents inefficace, délais longs, confinement inadéquat.
Besoins: Plan claire, procédures structurées, équipe formée, outils appropriés.`);

// MAJOR_INCIDENT_PLAN
export const PlanIncidentMajeurModal = createDocumentModal('MAJOR_INCIDENT_PLAN', {
  esprit: {
    heroTitle: "L'Esprit du Plan d'Incident Majeur",
    heroDescription: "Le Plan d'Incident Majeur est le <strong>guide d'urgence</strong> spécifique pour la gestion des incidents majeurs (P1/CAT 1). C'est l'outil qui transforme la crise en réponse coordonnée.",
    pourquoi: {
      probleme: "Sans plan d'incident majeur clair, les incidents critiques ne sont pas <strong>gérés efficacement</strong>, l'escalade est inadéquate, la communication est chaotique, les délais sont dépassés.",
      solution: "Le Plan d'Incident Majeur établit des <strong>procédures d'urgence</strong> spécifiques pour les incidents critiques, garantissant une réponse rapide et coordonnée."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident majeur est un <strong>incendie majeur</strong>, le Plan d'Incident Majeur est le <strong>plan d'intervention d'urgence</strong> qui définit:",
      points: [
        "Quand activer le plan (critères d'incident majeur)",
        "Qui intervient (équipe d'intervention majeure)",
        "Comment coordonner (commandant d'incident)",
        "Comment communiquer (communication d'urgence)",
        "Comment résoudre (workflows critiques)"
      ],
      conclusion: "Le Plan d'Incident Majeur, c'est exactement ça: le <strong>plan d'intervention d'urgence</strong> qui garantit que chaque incident critique est géré de manière efficace et coordonnée."
    },
    piliers: [
      { icon: AlertTriangle, title: '1. Définition & Activation', desc: 'Critères d\'incident majeur et procédures d\'activation', color: 'red' },
      { icon: UsersIcon, title: '2. Équipe d\'Intervention', desc: 'Composition et rôles de l\'équipe majeure', color: 'blue' },
      { icon: FileCheck, title: '3. Workflows Critiques', desc: 'Workflows et procédures critiques', color: 'green' },
      { icon: MessageSquare, title: '4. Communication d\'Urgence', desc: 'Communication d\'urgence et post-incident', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO/NIST', desc: 'Aligné sur les meilleurs cadres de gestion d\'incidents majeurs' },
      { title: 'Procédures d\'urgence', desc: 'Procédures spécifiques pour incidents critiques' },
      { title: 'Coordination efficace', desc: 'Commandant d\'incident pour coordination centrale' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au plan exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Plan d'Incident Majeur?",
    heroDescription: "Le Plan s'adresse principalement à l'<strong>équipe d'intervention majeure</strong> et à la <strong>direction</strong> qui doivent gérer les incidents critiques.",
    profils: [
      {
        title: 'Équipe d\'Intervention Majeure',
        icon: Shield,
        color: 'red',
        scenarios: [
          'Activer le plan rapidement',
          'Coordonner la réponse',
          'Exécuter les workflows critiques',
          'Communiquer efficacement'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Direction',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Comprendre les procédures d\'activation',
          'Valider les décisions critiques',
          'Superviser la communication',
          'Approuver les actions'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer le Plan d'Incident Majeur?",
    heroDescription: "Le Plan doit être livré <strong>en même temps</strong> que le Plan d'Intervention général pour établir les procédures spécifiques aux incidents majeurs.",
    moments: [
      {
        title: 'Mise en place processus incidents',
        icon: Rocket,
        color: 'green',
        description: 'Établir les procédures d\'urgence avec le processus principal'
      },
      {
        title: 'Après incident majeur',
        icon: AlertTriangle,
        color: 'red',
        description: 'Améliorer les procédures après un incident critique'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur du Plan d'Incident Majeur",
    heroDescription: "Le Plan transforme la gestion des incidents majeurs en <strong>processus coordonné</strong>, garantissant une réponse rapide et efficace.",
    avantages: [
      {
        title: 'Activation rapide',
        desc: 'Procédures claires pour activation immédiate',
        icon: Rocket,
        color: 'green'
      },
      {
        title: 'Coordination efficace',
        desc: 'Commandant d\'incident pour coordination centrale',
        icon: UsersIcon,
        color: 'blue'
      },
      {
        title: 'Workflows critiques',
        desc: 'Workflows spécifiques pour incidents majeurs',
        icon: FileCheck,
        color: 'orange'
      },
      {
        title: 'Communication d\'urgence',
        desc: 'Canaux et templates pour communication efficace',
        icon: MessageSquare,
        color: 'red'
      },
    ],
    roi: {
      title: 'ROI du Plan',
      description: 'Le Plan génère de la valeur en:',
      points: [
        'Réduisant les délais de résolution (améliore la productivité)',
        'Améliorant la coordination (maintient la confiance)',
        'Garantissant la communication (protège la réputation)',
        'Optimisant les ressources (allocation efficace)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité du Plan",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>plan exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Plan Synthèse',
        pages: '3-5 pages',
        contenu: 'Critères, activation, équipe, workflows de base',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Plan Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les procédures principales, workflows complets',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Plan Complet',
        pages: '10-15 pages',
        contenu: 'Procédures détaillées, workflows complets, communication',
        pour: 'Équipe d\'intervention (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Plan Géré',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, amélioration continue, scénarios',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Plan Optimisé',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, workflows avancés, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: incidents majeurs mal gérés, coordination inadéquate, communication chaotique.
Besoins: Plan claire, procédures d'urgence, équipe coordonnée, communication efficace.`);

// INCIDENT_COMMUNICATION_PLAN
export const PlanCommunicationIncidentModal = createDocumentModal('INCIDENT_COMMUNICATION_PLAN', {
  esprit: {
    heroTitle: "L'Esprit du Plan de Communication d'Incident",
    heroDescription: "Le Plan de Communication est le <strong>guide de communication</strong> qui définit les canaux, templates, audiences et processus de communication lors d'incidents. C'est l'outil qui transforme le chaos communicationnel en message structuré.",
    pourquoi: {
      probleme: "Sans plan de communication clair, la communication lors d'incidents est <strong>chaotique</strong>, les messages sont incohérents, les audiences ne sont pas informées, la réputation est menacée.",
      solution: "Le Plan de Communication établit des <strong>canaux et templates structurés</strong> pour la communication interne et externe, garantissant des messages cohérents et transparents."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident est un <strong>accident</strong>, le Plan de Communication est le <strong>plan de communication de crise</strong> qui définit:",
      points: [
        "Qui informe-t-on (audiences internes/externes)",
        "Comment on informe (canaux de communication)",
        "Quand on informe (fréquence de communication)",
        "Quoi dire (templates et messages)",
        "Qui approuve (processus d'approbation)"
      ],
      conclusion: "Le Plan de Communication, c'est exactement ça: le <strong>plan de communication de crise</strong> qui garantit que chaque audience est informée de manière cohérente et transparente."
    },
    piliers: [
      { icon: UsersIcon, title: '1. Audiences', desc: 'Audiences internes et externes', color: 'blue' },
      { icon: MessageSquare, title: '2. Canaux & Méthodes', desc: 'Canaux et méthodes de communication', color: 'green' },
      { icon: FileText, title: '3. Templates & Messages', desc: 'Templates et messages de communication', color: 'orange' },
      { icon: AlertTriangle, title: '4. Processus & Gestion', desc: 'Processus de communication et gestion de crise', color: 'red' },
    ],
    prismeAdvantages: [
      { title: 'Conforme NIST CSF 2.0/ITIL/ISO/COBIT', desc: 'Aligné sur les meilleurs cadres de communication d\'incidents' },
      { title: 'Communication structurée', desc: 'Canaux et templates pour communication cohérente' },
      { title: 'Transparence garantie', desc: 'Processus pour communication transparente' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au plan exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Plan de Communication?",
    heroDescription: "Le Plan s'adresse principalement au <strong>responsable communication</strong> et à l'<strong>équipe de gestion d'incidents</strong> qui doivent communiquer lors d'incidents.",
    profils: [
      {
        title: 'Responsable Communication',
        icon: MessageSquare,
        color: 'green',
        scenarios: [
          'Utiliser les canaux appropriés',
          'Appliquer les templates',
          'Gérer les audiences',
          'Coordonner la communication'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Équipe Gestion d\'Incidents',
        icon: Shield,
        color: 'blue',
        scenarios: [
          'Comprendre les processus de communication',
          'Utiliser les templates',
          'Respecter les fréquences',
          'Suivre les approbations'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer le Plan de Communication?",
    heroDescription: "Le Plan doit être livré <strong>en même temps</strong> que le Plan d'Intervention pour établir les processus de communication.",
    moments: [
      {
        title: 'Mise en place processus incidents',
        icon: Rocket,
        color: 'green',
        description: 'Établir les processus de communication avec le processus principal'
      },
      {
        title: 'Problèmes de communication',
        icon: AlertTriangle,
        color: 'red',
        description: 'Résoudre les problèmes de communication incohérente'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur du Plan de Communication",
    heroDescription: "Le Plan transforme la communication en <strong>processus structuré</strong>, garantissant des messages cohérents et transparents.",
    avantages: [
      {
        title: 'Communication cohérente',
        desc: 'Templates pour messages cohérents',
        icon: MessageSquare,
        color: 'green'
      },
      {
        title: 'Transparence garantie',
        desc: 'Processus pour communication transparente',
        icon: Eye,
        color: 'blue'
      },
      {
        title: 'Gestion de crise',
        desc: 'Canaux et processus pour gestion de crise',
        icon: AlertTriangle,
        color: 'red'
      },
      {
        title: 'Protection réputation',
        desc: 'Communication efficace pour protéger la réputation',
        icon: Shield,
        color: 'orange'
      },
    ],
    roi: {
      title: 'ROI du Plan',
      description: 'Le Plan génère de la valeur en:',
      points: [
        'Améliorant la communication (maintient la confiance)',
        'Protégeant la réputation (réduit les dommages)',
        'Garantissant la transparence (respecte les obligations)',
        'Optimisant les ressources (allocation efficace)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité du Plan",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>plan exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Plan Synthèse',
        pages: '3-5 pages',
        contenu: 'Audiences, canaux principaux, templates de base',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Plan Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, canaux complets, templates',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Plan Complet',
        pages: '10-15 pages',
        contenu: 'Templates détaillés, processus complets, gestion médias',
        pour: 'Responsable communication (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Plan Géré',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, amélioration continue, scénarios',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Plan Optimisé',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, templates avancés, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: communication incohérente, messages contradictoires, réputation menacée.
Besoins: Plan claire, canaux définis, templates prêts, processus structurés.`);

// ITSCM_PLAN
export const PlanContinuiteTiModal = createDocumentModal('ITSCM_PLAN', {
  esprit: {
    heroTitle: "L'Esprit du Plan de Continuité TI (ITSCM)",
    heroDescription: "Le Plan de Continuité TI est le <strong>guide de reprise</strong> qui définit les stratégies de sauvegarde, restauration, sites de secours et processus de continuité. C'est l'outil qui transforme le sinistre en reprise rapide.",
    pourquoi: {
      probleme: "Sans plan de continuité clair, les sinistres causent des <strong>interruptions prolongées</strong>, les données sont perdues, les services ne sont pas restaurés, l'entreprise est paralysée.",
      solution: "Le Plan de Continuité établit des <strong>stratégies de reprise</strong> complètes pour assurer la continuité des services TI, garantissant une reprise rapide après sinistre."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un sinistre est un <strong>tremblement de terre</strong>, le Plan de Continuité TI est le <strong>plan de reconstruction</strong> qui définit:",
      points: [
        "Quels services restaurer en premier (priorisation)",
        "Comment restaurer (stratégies de restauration)",
        "Où restaurer (sites de secours)",
        "Quand restaurer (RTO - délais cibles)",
        "Depuis quoi restaurer (RPO - point de récupération)"
      ],
      conclusion: "Le Plan de Continuité TI, c'est exactement ça: le <strong>plan de reconstruction</strong> qui garantit que chaque service est restauré rapidement après un sinistre."
    },
    piliers: [
      { icon: AlertTriangle, title: '1. Services Critiques', desc: 'Identification et priorisation des services critiques', color: 'red' },
      { icon: Shield, title: '2. Sauvegarde & Restauration', desc: 'Stratégies de sauvegarde et restauration', color: 'blue' },
      { icon: Building2, title: '3. Sites de Secours', desc: 'Sites de secours et infrastructure', color: 'green' },
      { icon: CheckCircle2, title: '4. Processus & Tests', desc: 'Processus de continuité et tests', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO/ISO 22301', desc: 'Aligné sur les meilleurs cadres de continuité' },
      { title: 'Stratégies complètes', desc: 'Sauvegarde, restauration, sites de secours' },
      { title: 'RTO/RPO définis', desc: 'Délais cibles pour restauration' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au plan exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Plan de Continuité TI?",
    heroDescription: "Le Plan s'adresse principalement à l'<strong>équipe TI</strong> et à la <strong>direction</strong> qui doivent assurer la continuité des services.",
    profils: [
      {
        title: 'Équipe TI',
        icon: Shield,
        color: 'green',
        scenarios: [
          'Implémenter les stratégies de sauvegarde',
          'Gérer les sites de secours',
          'Exécuter les processus de restauration',
          'Effectuer les tests'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Direction',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Comprendre les stratégies',
          'Valider les investissements',
          'Approuver les RTO/RPO',
          'Décider des priorités'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer le Plan de Continuité TI?",
    heroDescription: "Le Plan doit être livré <strong>avant</strong> tout sinistre pour que l'organisation soit préparée.",
    moments: [
      {
        title: 'Mise en place continuité',
        icon: Rocket,
        color: 'green',
        description: 'Établir les stratégies de continuité'
      },
      {
        title: 'Amélioration continuité existante',
        icon: Zap,
        color: 'yellow',
        description: 'Structurer et améliorer la continuité existante'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur du Plan de Continuité TI",
    heroDescription: "Le Plan transforme la continuité en <strong>stratégie structurée</strong>, garantissant une reprise rapide après sinistre.",
    avantages: [
      {
        title: 'Reprise rapide',
        desc: 'Stratégies pour reprise rapide des services',
        icon: Rocket,
        color: 'green'
      },
      {
        title: 'Protection des données',
        desc: 'Sauvegardes pour protection des données',
        icon: Shield,
        color: 'blue'
      },
      {
        title: 'Sites de secours',
        desc: 'Infrastructure de secours pour continuité',
        icon: Building2,
        color: 'orange'
      },
      {
        title: 'Tests réguliers',
        desc: 'Tests pour valider les stratégies',
        icon: CheckCircle2,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI du Plan',
      description: 'Le Plan génère de la valeur en:',
      points: [
        'Réduisant les interruptions (améliore la productivité)',
        'Protégeant les données (évite les pertes)',
        'Garantissant la continuité (maintient les services)',
        'Optimisant les investissements (allocation efficace)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité du Plan",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>plan exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Plan Synthèse',
        pages: '3-5 pages',
        contenu: 'Services critiques, stratégies de base, RTO/RPO principaux',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Plan Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, stratégies complètes',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Plan Complet',
        pages: '10-15 pages',
        contenu: 'Stratégies détaillées, processus complets, sites de secours',
        pour: 'Équipe TI (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Plan Géré',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, amélioration continue, scénarios de tests',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Plan Optimisé',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, stratégies avancées, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, dépendance aux fournisseurs.
Enjeux TI: interruptions prolongées, perte de données, services non restaurés.
Besoins: Plan claire, stratégies de sauvegarde, sites de secours, tests réguliers.`);

// PRIVACY_INCIDENT_PLAN
export const PlanIncidentViePriveeModal = createDocumentModal('PRIVACY_INCIDENT_PLAN', {
  esprit: {
    heroTitle: "L'Esprit du Plan d'Incident Vie Privée / Confidentialité",
    heroDescription: "Le Plan d'Incident Vie Privée est le <strong>guide de conformité</strong> qui définit les procédures de détection, évaluation, notification CAI, avis aux personnes concernées et mesures correctives. C'est l'outil qui transforme la violation en conformité.",
    pourquoi: {
      probleme: "Sans plan d'incident vie privée clair, les violations ne sont pas <strong>détectées rapidement</strong>, les notifications CAI sont tardives, les personnes concernées ne sont pas avisées, les sanctions sont appliquées.",
      solution: "Le Plan d'Incident Vie Privée établit des <strong>procédures de conformité</strong> complètes pour la gestion des incidents de protection des renseignements personnels, garantissant le respect des obligations légales."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si une violation de vie privée est un <strong>accident de voiture</strong>, le Plan d'Incident Vie Privée est le <strong>protocole d'intervention</strong> qui définit:",
      points: [
        "Comment détecter l'accident (sources de détection)",
        "Comment évaluer les dommages (évaluation sévérité)",
        "Comment notifier les autorités (notification CAI)",
        "Comment aviser les personnes concernées (avis personnes)",
        "Comment corriger (mesures correctives)"
      ],
      conclusion: "Le Plan d'Incident Vie Privée, c'est exactement ça: le <strong>protocole d'intervention</strong> qui garantit que chaque violation est gérée conformément aux obligations légales."
    },
    piliers: [
      { icon: Eye, title: '1. Détection & Évaluation', desc: 'Détection et évaluation des incidents vie privée', color: 'blue' },
      { icon: FileCheck, title: '2. Notification CAI', desc: 'Procédures de notification CAI', color: 'red' },
      { icon: MessageSquare, title: '3. Avis Personnes', desc: 'Avis aux personnes concernées', color: 'green' },
      { icon: Shield, title: '4. Mesures Correctives', desc: 'Mesures correctives et documentation', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Conforme Loi 25/RGPD/ISO/ITIL', desc: 'Aligné sur les obligations légales et standards' },
      { title: 'Conformité garantie', desc: 'Procédures pour respecter les obligations légales' },
      { title: 'Notification structurée', desc: 'Processus pour notification CAI et avis personnes' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au plan exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Plan d'Incident Vie Privée?",
    heroDescription: "Le Plan s'adresse principalement au <strong>DPO</strong> et à l'<strong>équipe de conformité</strong> qui doivent gérer les incidents de protection des renseignements personnels.",
    profils: [
      {
        title: 'DPO / Responsable Conformité',
        icon: FileCheck,
        color: 'purple',
        scenarios: [
          'Détecter les incidents vie privée',
          'Évaluer la sévérité',
          'Notifier la CAI',
          'Aviser les personnes concernées'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Équipe TI / Sécurité',
        icon: Shield,
        color: 'blue',
        scenarios: [
          'Détecter les incidents',
          'Appliquer les mesures correctives',
          'Documenter les incidents',
          'Assurer la conformité'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer le Plan d'Incident Vie Privée?",
    heroDescription: "Le Plan doit être livré <strong>avant</strong> toute violation pour que l'organisation soit préparée à répondre conformément.",
    moments: [
      {
        title: 'Conformité Loi 25',
        icon: FileCheck,
        color: 'green',
        description: 'Répondre aux obligations Loi 25'
      },
      {
        title: 'Après violation',
        icon: AlertTriangle,
        color: 'red',
        description: 'Améliorer les procédures après une violation'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur du Plan d'Incident Vie Privée",
    heroDescription: "Le Plan transforme la gestion des incidents vie privée en <strong>processus conforme</strong>, garantissant le respect des obligations légales.",
    avantages: [
      {
        title: 'Conformité garantie',
        desc: 'Procédures pour respecter les obligations légales',
        icon: FileCheck,
        color: 'green'
      },
      {
        title: 'Notification rapide',
        desc: 'Processus pour notification CAI dans les délais',
        icon: Clock,
        color: 'blue'
      },
      {
        title: 'Avis efficace',
        desc: 'Processus pour avis aux personnes concernées',
        icon: MessageSquare,
        color: 'orange'
      },
      {
        title: 'Protection réputation',
        desc: 'Gestion conforme pour protéger la réputation',
        icon: Shield,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI du Plan',
      description: 'Le Plan génère de la valeur en:',
      points: [
        'Évitant les sanctions (respecte les obligations légales)',
        'Protégeant la réputation (gestion conforme)',
        'Réduisant les risques (détection rapide)',
        'Optimisant les ressources (allocation efficace)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité du Plan",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>plan exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Plan Synthèse',
        pages: '3-5 pages',
        contenu: 'Détection, évaluation, notification CAI de base, avis personnes',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Plan Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, procédures complètes',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Plan Complet',
        pages: '10-15 pages',
        contenu: 'Procédures détaillées, processus complets, documentation',
        pour: 'DPO (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Plan Géré',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, amélioration continue, scénarios',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Plan Optimisé',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, procédures avancées, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, données clients sensibles.
Enjeux TI: conformité Loi 25, protection renseignements personnels, risques de violations.
Besoins: Plan claire, procédures conformes, notification CAI, avis personnes.`);

// INCIDENT_MANAGEMENT_PROCEDURE
export const ProcedureGestionIncidentsModal = createDocumentModal('INCIDENT_MANAGEMENT_PROCEDURE', {
  esprit: {
    heroTitle: "L'Esprit de la Procédure de Gestion des Incidents (ITIL)",
    heroDescription: "La Procédure de Gestion des Incidents est le <strong>guide opérationnel détaillé</strong> qui définit les étapes, workflows, rôles et responsabilités pour la gestion des incidents TI selon ITIL 4. C'est l'outil qui transforme la théorie en pratique quotidienne.",
    pourquoi: {
      probleme: "Sans procédure opérationnelle claire, les incidents ne sont pas <strong>gérés de manière cohérente</strong>, les workflows sont incohérents, les délais ne sont pas respectés, la satisfaction client diminue.",
      solution: "La Procédure établit des <strong>workflows structurés</strong> pour l'enregistrement, classification, résolution et fermeture, garantissant une gestion efficace et cohérente."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si la gestion d'incidents est une <strong>recette de cuisine</strong>, la Procédure est le <strong>manuel de recettes détaillé</strong> qui définit:",
      points: [
        "Les ingrédients nécessaires (outils, rôles)",
        "Les étapes précises (workflows)",
        "Les temps de cuisson (délais)",
        "Les techniques (méthodes)",
        "Les vérifications (critères de fermeture)"
      ],
      conclusion: "La Procédure, c'est exactement ça: le <strong>manuel de recettes</strong> qui garantit que chaque incident est géré de manière cohérente et efficace."
    },
    piliers: [
      { icon: FileText, title: '1. Enregistrement', desc: 'Processus d\'enregistrement et canaux', color: 'blue' },
      { icon: Target, title: '2. Classification', desc: 'Classification et priorisation', color: 'green' },
      { icon: FileCheck, title: '3. Résolution', desc: 'Workflows de résolution par niveau', color: 'orange' },
      { icon: CheckCircle2, title: '4. Fermeture', desc: 'Processus de fermeture et amélioration', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL 4/COBIT/ISO', desc: 'Alignée sur les meilleurs cadres de gestion d\'incidents' },
      { title: 'Workflows détaillés', desc: 'Étapes précises pour chaque phase de gestion' },
      { title: 'Opérationnelle', desc: 'Document prêt à être utilisé au quotidien' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la procédure exhaustive (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Procédure de Gestion des Incidents?",
    heroDescription: "La Procédure s'adresse principalement à l'<strong>équipe TI</strong> et aux <strong>gestionnaires d'incidents</strong> qui doivent gérer les incidents au quotidien.",
    profils: [
      {
        title: 'Équipe TI / Support',
        icon: Shield,
        color: 'green',
        scenarios: [
          'Enregistrer les incidents',
          'Classifier correctement',
          'Résoudre selon les workflows',
          'Fermer selon les critères'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Superviser les workflows',
          'Valider les classifications',
          'Gérer les escalades',
          'Améliorer les processus'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Procédure de Gestion des Incidents?",
    heroDescription: "La Procédure doit être livrée <strong>après</strong> la Politique de Gestion des Incidents pour définir les workflows opérationnels.",
    moments: [
      {
        title: 'Mise en place processus incidents',
        icon: Rocket,
        color: 'green',
        description: 'Définir les workflows opérationnels après la politique'
      },
      {
        title: 'Amélioration processus existant',
        icon: Zap,
        color: 'yellow',
        description: 'Structurer et améliorer les workflows existants'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Procédure de Gestion des Incidents",
    heroDescription: "La Procédure transforme la gestion des incidents en <strong>processus opérationnel cohérent</strong>, garantissant une gestion efficace et standardisée.",
    avantages: [
      {
        title: 'Cohérence garantie',
        desc: 'Workflows standardisés pour gestion cohérente',
        icon: FileCheck,
        color: 'green'
      },
      {
        title: 'Efficacité améliorée',
        desc: 'Étapes claires pour résolution rapide',
        icon: Clock,
        color: 'blue'
      },
      {
        title: 'Satisfaction client',
        desc: 'Délais respectés pour satisfaction client',
        icon: CheckCircle2,
        color: 'orange'
      },
      {
        title: 'Amélioration continue',
        desc: 'Processus de révision pour amélioration',
        icon: TrendingUp,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI de la Procédure',
      description: 'La Procédure génère de la valeur en:',
      points: [
        'Améliorant la cohérence (réduit les erreurs)',
        'Réduisant les délais (améliore la productivité)',
        'Augmentant la satisfaction client (maintient la confiance)',
        'Optimisant les ressources (allocation efficace)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Procédure",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>procédure exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Procédure Synthèse',
        pages: '3-5 pages',
        contenu: 'Étapes principales, workflows de base',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Procédure Structurée',
        pages: '6-10 pages',
        contenu: 'Toutes les étapes principales, workflows complets',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Procédure Complète',
        pages: '10-15 pages',
        contenu: 'Workflows détaillés, exemples concrets, rôles précis',
        pour: 'Équipe TI (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Procédure Gérée',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, amélioration continue, scénarios',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Procédure Optimisée',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, workflows avancés, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: gestion incohérente, workflows non standardisés, délais non respectés.
Besoins: Procédure claire, workflows structurés, rôles définis, formation.`);

// SECURITY_INCIDENT_RESPONSE_PROCEDURE
export const ProcedureReponseIncidentsSecuriteModal = createDocumentModal('SECURITY_INCIDENT_RESPONSE_PROCEDURE', {
  esprit: {
    heroTitle: "L'Esprit de la Procédure de Réponse aux Incidents de Sécurité (NIST)",
    heroDescription: "La Procédure de Réponse aux Incidents de Sécurité est le <strong>guide opérationnel détaillé</strong> qui définit les étapes, workflows et actions pour répondre aux incidents de sécurité selon NIST CSF 2.0. C'est l'outil qui transforme la réponse en processus structuré.",
    pourquoi: {
      probleme: "Sans procédure opérationnelle claire, les incidents de sécurité ne sont pas <strong>répondus efficacement</strong>, les mesures de confinement sont inadéquates, l'éradication est incomplète, les données sont compromises.",
      solution: "La Procédure établit des <strong>workflows structurés</strong> pour la détection, analyse, confinement, éradication et restauration, garantissant une réponse rapide et efficace."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident de sécurité est un <strong>feu</strong>, la Procédure est le <strong>manuel d'intervention détaillé</strong> qui définit:",
      points: [
        "Comment détecter le feu (sources de détection)",
        "Comment analyser la situation (processus d'analyse)",
        "Comment confiner le feu (mesures de confinement)",
        "Comment éteindre le feu (mesures d'éradication)",
        "Comment restaurer les dommages (processus de restauration)"
      ],
      conclusion: "La Procédure, c'est exactement ça: le <strong>manuel d'intervention</strong> qui garantit que chaque incident de sécurité est répondu de manière efficace et structurée."
    },
    piliers: [
      { icon: Eye, title: '1. Détection & Analyse', desc: 'Processus de détection et d\'analyse', color: 'blue' },
      { icon: Shield, title: '2. Confinement & Éradication', desc: 'Mesures de confinement et d\'éradication', color: 'red' },
      { icon: CheckCircle2, title: '3. Restauration', desc: 'Processus de restauration et post-incident', color: 'green' },
      { icon: FileText, title: '4. Documentation', desc: 'Documentation et amélioration continue', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Conforme NIST CSF 2.0/ITIL/ISO', desc: 'Alignée sur les meilleurs cadres de réponse aux incidents' },
      { title: 'Workflows détaillés', desc: 'Étapes précises pour chaque phase de réponse' },
      { title: 'Opérationnelle', desc: 'Document prêt à être utilisé en situation d\'urgence' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la procédure exhaustive (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Procédure de Réponse aux Incidents de Sécurité?",
    heroDescription: "La Procédure s'adresse principalement à l'<strong>équipe d'intervention</strong> et aux <strong>gestionnaires d'incidents</strong> qui doivent répondre aux incidents de sécurité.",
    profils: [
      {
        title: 'Équipe d\'Intervention',
        icon: Shield,
        color: 'red',
        scenarios: [
          'Utiliser les workflows de détection',
          'Appliquer les mesures de confinement',
          'Exécuter les processus d\'éradication',
          'Effectuer la restauration'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Coordonner l\'équipe',
          'Superviser les workflows',
          'Valider les actions',
          'Gérer la documentation'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Procédure de Réponse aux Incidents de Sécurité?",
    heroDescription: "La Procédure doit être livrée <strong>après</strong> le Plan d'Intervention pour définir les workflows opérationnels détaillés.",
    moments: [
      {
        title: 'Mise en place réponse incidents',
        icon: Rocket,
        color: 'green',
        description: 'Définir les workflows opérationnels après le plan'
      },
      {
        title: 'Amélioration processus existant',
        icon: Zap,
        color: 'yellow',
        description: 'Structurer et améliorer les workflows existants'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Procédure de Réponse aux Incidents de Sécurité",
    heroDescription: "La Procédure transforme la réponse aux incidents en <strong>processus opérationnel structuré</strong>, garantissant une réponse rapide et efficace.",
    avantages: [
      {
        title: 'Réponse rapide',
        desc: 'Workflows clairs pour réponse immédiate',
        icon: Clock,
        color: 'green'
      },
      {
        title: 'Confinement efficace',
        desc: 'Mesures de confinement pour limiter l\'impact',
        icon: Shield,
        color: 'red'
      },
      {
        title: 'Éradication complète',
        desc: 'Processus d\'éradication pour éliminer la menace',
        icon: CheckCircle2,
        color: 'blue'
      },
      {
        title: 'Restauration rapide',
        desc: 'Processus de restauration pour rétablir les services',
        icon: Rocket,
        color: 'orange'
      },
    ],
    roi: {
      title: 'ROI de la Procédure',
      description: 'La Procédure génère de la valeur en:',
      points: [
        'Réduisant les délais de résolution (améliore la productivité)',
        'Limitant l\'impact des incidents (réduit les dommages)',
        'Garantissant l\'éradication complète (prévient les récurrences)',
        'Optimisant la restauration (minimise les interruptions)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Procédure",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>procédure exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Procédure Synthèse',
        pages: '3-5 pages',
        contenu: 'Étapes principales, workflows de base',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Procédure Structurée',
        pages: '6-10 pages',
        contenu: 'Toutes les étapes principales, workflows complets',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Procédure Complète',
        pages: '10-15 pages',
        contenu: 'Workflows détaillés, exemples concrets, outils',
        pour: 'Équipe d\'intervention (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Procédure Gérée',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, amélioration continue, scénarios',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Procédure Optimisée',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, workflows avancés, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents de sécurité fréquents.
Enjeux TI: réponse inefficace, confinement inadéquat, éradication incomplète.
Besoins: Procédure claire, workflows structurés, équipe formée, outils appropriés.`);

// ESCALATION_PROCEDURE
export const ProcedureEscaladeModal = createDocumentModal('ESCALATION_PROCEDURE', {
  esprit: {
    heroTitle: "L'Esprit de la Procédure d'Escalade",
    heroDescription: "La Procédure d'Escalade est le <strong>guide opérationnel détaillé</strong> qui définit les critères, délais, responsables et workflows pour l'escalade des incidents selon ITIL 4. C'est l'outil qui transforme l'escalade en processus structuré.",
    pourquoi: {
      probleme: "Sans procédure d'escalade claire, les incidents ne sont pas <strong>escaladés efficacement</strong>, les délais sont dépassés, les responsables ne sont pas identifiés, la communication est inadéquate.",
      solution: "La Procédure établit des <strong>workflows structurés</strong> pour l'escalade hiérarchique et fonctionnelle, garantissant une escalade rapide et efficace."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si l'escalade est un <strong>relais</strong>, la Procédure est le <strong>manuel de relais</strong> qui définit:",
      points: [
        "Quand passer le relais (critères d'escalade)",
        "À qui passer le relais (responsables)",
        "Comment passer le relais (communication)",
        "Dans quel délai (délais d'escalade)",
        "Comment suivre (documentation)"
      ],
      conclusion: "La Procédure d'Escalade, c'est exactement ça: le <strong>manuel de relais</strong> qui garantit que chaque incident est escaladé au bon moment et à la bonne personne."
    },
    piliers: [
      { icon: Building2, title: '1. Structure', desc: 'Structure et niveaux d\'escalade', color: 'blue' },
      { icon: Clock, title: '2. Critères & Délais', desc: 'Critères et délais d\'escalade', color: 'orange' },
      { icon: MessageSquare, title: '3. Communication', desc: 'Communication et documentation', color: 'green' },
      { icon: CheckCircle2, title: '4. Amélioration', desc: 'Amélioration continue', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO', desc: 'Alignée sur les meilleurs cadres d\'escalade' },
      { title: 'Workflows détaillés', desc: 'Étapes précises pour chaque niveau d\'escalade' },
      { title: 'Opérationnelle', desc: 'Document prêt à être utilisé au quotidien' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la procédure exhaustive (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Procédure d'Escalade?",
    heroDescription: "La Procédure s'adresse principalement à l'<strong>équipe TI</strong> et aux <strong>gestionnaires d'incidents</strong> qui doivent escalader les incidents.",
    profils: [
      {
        title: 'Équipe TI / Support',
        icon: Shield,
        color: 'green',
        scenarios: [
          'Escalader au bon moment',
          'Identifier les responsables',
          'Respecter les délais',
          'Communiquer efficacement'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Superviser l\'escalade',
          'Valider les escalades',
          'Gérer les workflows',
          'Améliorer le processus'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Procédure d'Escalade?",
    heroDescription: "La Procédure doit être livrée <strong>après</strong> la Politique d'Escalade pour définir les workflows opérationnels détaillés.",
    moments: [
      {
        title: 'Mise en place processus incidents',
        icon: Rocket,
        color: 'green',
        description: 'Définir les workflows d\'escalade après la politique'
      },
      {
        title: 'Problèmes d\'escalade',
        icon: AlertTriangle,
        color: 'red',
        description: 'Résoudre les problèmes d\'escalade inefficace'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Procédure d'Escalade",
    heroDescription: "La Procédure transforme l'escalade en <strong>processus opérationnel structuré</strong>, garantissant une escalade rapide et efficace.",
    avantages: [
      {
        title: 'Escalade efficace',
        desc: 'Workflows structurés pour escalade rapide',
        icon: Rocket,
        color: 'green'
      },
      {
        title: 'Délais respectés',
        desc: 'Délais d\'escalade définis et mesurables',
        icon: Clock,
        color: 'blue'
      },
      {
        title: 'Communication claire',
        desc: 'Canaux et templates pour communication efficace',
        icon: MessageSquare,
        color: 'orange'
      },
      {
        title: 'Responsabilités claires',
        desc: 'Responsables identifiés à chaque niveau',
        icon: UsersIcon,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI de la Procédure',
      description: 'La Procédure génère de la valeur en:',
      points: [
        'Réduisant les délais d\'escalade (améliore la résolution)',
        'Améliorant la communication (maintient la confiance)',
        'Optimisant les ressources (allocation efficace)',
        'Garantissant la responsabilité (imputabilité claire)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Procédure",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>procédure exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Procédure Synthèse',
        pages: '3-5 pages',
        contenu: 'Structure, critères principaux, délais de base',
        pour: 'Équipe TI (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Procédure Structurée',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, workflows complets',
        pour: 'Support (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Procédure Complète',
        pages: '10-15 pages',
        contenu: 'Workflows détaillés, matrices de décision, exemples concrets',
        pour: 'Gestionnaires (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Procédure Gérée',
        pages: '15-20 pages',
        contenu: 'Métriques d\'escalade, KPIs, amélioration continue',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Procédure Optimisée',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, workflows avancés, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: escalade inefficace, délais dépassés, responsables non identifiés.
Besoins: Procédure claire, workflows structurés, responsables identifiés, communication efficace.`);

// EVIDENCE_MANAGEMENT_PROCEDURE
export const ProcedureGestionPreuvesModal = createDocumentModal('EVIDENCE_MANAGEMENT_PROCEDURE', {
  esprit: {
    heroTitle: "L'Esprit de la Procédure de Gestion des Preuves",
    heroDescription: "La Procédure de Gestion des Preuves est le <strong>guide opérationnel détaillé</strong> qui définit les processus de collecte, préservation, analyse et documentation des preuves numériques selon les standards forensiques. C'est l'outil qui transforme la collecte en preuve valide.",
    pourquoi: {
      probleme: "Sans procédure de gestion des preuves claire, les preuves ne sont pas <strong>collectées correctement</strong>, la chaîne de traçabilité est brisée, les preuves sont invalides, les investigations échouent.",
      solution: "La Procédure établit des <strong>processus structurés</strong> pour la collecte, préservation, analyse et documentation, garantissant des preuves valides et utilisables."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si une preuve numérique est une <strong>empreinte digitale</strong>, la Procédure est le <strong>protocole de collecte</strong> qui définit:",
      points: [
        "Comment collecter l'empreinte (processus de collecte)",
        "Comment préserver l'empreinte (préservation)",
        "Comment analyser l'empreinte (analyse)",
        "Comment documenter l'empreinte (documentation)",
        "Comment maintenir la chaîne (chaîne de traçabilité)"
      ],
      conclusion: "La Procédure de Gestion des Preuves, c'est exactement ça: le <strong>protocole de collecte</strong> qui garantit que chaque preuve est valide et utilisable."
    },
    piliers: [
      { icon: Shield, title: '1. Collecte & Préservation', desc: 'Processus de collecte et préservation', color: 'blue' },
      { icon: FileText, title: '2. Analyse & Documentation', desc: 'Processus d\'analyse et documentation', color: 'green' },
      { icon: Building2, title: '3. Gestion & Stockage', desc: 'Gestion et stockage sécurisé', color: 'orange' },
      { icon: CheckCircle2, title: '4. Amélioration', desc: 'Amélioration continue', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/ISO/NIST', desc: 'Alignée sur les standards forensiques' },
      { title: 'Chaîne de traçabilité', desc: 'Processus pour maintenir la chaîne de traçabilité' },
      { title: 'Preuves valides', desc: 'Collecte et préservation pour validité légale' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la procédure exhaustive (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Procédure de Gestion des Preuves?",
    heroDescription: "La Procédure s'adresse principalement à l'<strong>équipe sécurité</strong> et aux <strong>experts forensiques</strong> qui doivent gérer les preuves lors d'incidents.",
    profils: [
      {
        title: 'Équipe Sécurité / Experts Forensiques',
        icon: Shield,
        color: 'purple',
        scenarios: [
          'Collecter les preuves correctement',
          'Préserver l\'intégrité',
          'Analyser les preuves',
          'Documenter la chaîne de traçabilité'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Superviser la collecte',
          'Valider les processus',
          'Gérer le stockage',
          'Assurer la conformité'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer la Procédure de Gestion des Preuves?",
    heroDescription: "La Procédure doit être livrée <strong>avant</strong> tout incident nécessitant une investigation pour que l'équipe soit préparée.",
    moments: [
      {
        title: 'Mise en place investigation',
        icon: Rocket,
        color: 'green',
        description: 'Établir les processus de gestion des preuves'
      },
      {
        title: 'Amélioration processus existant',
        icon: Zap,
        color: 'yellow',
        description: 'Structurer et améliorer les processus existants'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de la Procédure de Gestion des Preuves",
    heroDescription: "La Procédure transforme la gestion des preuves en <strong>processus conforme</strong>, garantissant des preuves valides et utilisables.",
    avantages: [
      {
        title: 'Preuves valides',
        desc: 'Collecte et préservation pour validité légale',
        icon: FileCheck,
        color: 'green'
      },
      {
        title: 'Chaîne de traçabilité',
        desc: 'Processus pour maintenir la chaîne de traçabilité',
        icon: Shield,
        color: 'blue'
      },
      {
        title: 'Investigation efficace',
        desc: 'Analyse appropriée pour investigation efficace',
        icon: Eye,
        color: 'orange'
      },
      {
        title: 'Conformité garantie',
        desc: 'Processus conformes aux standards forensiques',
        icon: CheckCircle2,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI de la Procédure',
      description: 'La Procédure génère de la valeur en:',
      points: [
        'Garantissant la validité des preuves (permet les investigations)',
        'Maintenant la chaîne de traçabilité (protège la validité légale)',
        'Améliorant l\'efficacité des investigations (réduit les délais)',
        'Assurant la conformité (évite les rejets de preuves)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de la Procédure",
    heroDescription: "Du <strong>résumé exécutif</strong> à la <strong>procédure exhaustive</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Procédure Synthèse',
        pages: '3-5 pages',
        contenu: 'Processus de base, chaîne de traçabilité essentielle',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Procédure Structurée',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, processus complets',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Procédure Complète',
        pages: '10-15 pages',
        contenu: 'Processus détaillés, outils, exemples concrets',
        pour: 'Équipe sécurité (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Procédure Gérée',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, amélioration continue, scénarios',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Procédure Optimisée',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, processus avancés, plan d\'amélioration structuré',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents nécessitant investigation.
Enjeux TI: collecte incorrecte, chaîne de traçabilité brisée, preuves invalides.
Besoins: Procédure claire, processus structurés, chaîne de traçabilité, conformité.`);

// POST_INCIDENT_REVIEW
export const PostIncidentReviewModal = createDocumentModal('POST_INCIDENT_REVIEW', {
  esprit: {
    heroTitle: "L'Esprit du Post-Incident Review (PIR)",
    heroDescription: "Le Post-Incident Review est le <strong>document d'analyse post-incident</strong> qui examine les causes, la réponse, les impacts et les leçons apprises d'un incident. C'est l'outil qui transforme les incidents en opportunités d'amélioration.",
    pourquoi: {
      probleme: "Sans analyse post-incident, les incidents se répètent, les mêmes erreurs sont commises, les processus ne s'améliorent pas, l'organisation ne progresse pas.",
      solution: "Le Post-Incident Review établit une <strong>analyse structurée</strong> pour comprendre les causes, évaluer la réponse, mesurer l'impact et identifier les améliorations."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident est un <strong>accident de voiture</strong>, le Post-Incident Review est le <strong>rapport d'enquête détaillé</strong> qui examine:",
      points: [
        "Les causes de l'accident (cause racine)",
        "Comment l'accident a été géré (réponse)",
        "Les dommages causés (impact)",
        "Comment éviter que cela se reproduise (leçons apprises)"
      ],
      conclusion: "Le Post-Incident Review, c'est exactement ça: le <strong>rapport d'enquête</strong> qui garantit que chaque incident devient une opportunité d'amélioration."
    },
    piliers: [
      { icon: Eye, title: '1. Analyse', desc: 'Analyse de cause racine et impact', color: 'blue' },
      { icon: Clock, title: '2. Chronologie', desc: 'Chronologie et processus de détection', color: 'green' },
      { icon: FileCheck, title: '3. Réponse', desc: 'Réponse et résolution', color: 'orange' },
      { icon: TrendingUp, title: '4. Amélioration', desc: 'Leçons apprises et amélioration', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO/NIST', desc: 'Aligné sur les meilleurs cadres d\'analyse post-incident' },
      { title: 'Analyse approfondie', desc: 'Examen détaillé des causes et impacts' },
      { title: 'Amélioration continue', desc: 'Plan d\'amélioration structuré' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au rapport exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Post-Incident Review?",
    heroDescription: "Le Post-Incident Review s'adresse principalement à la <strong>direction</strong> et aux <strong>gestionnaires d'incidents</strong> pour l'amélioration continue.",
    profils: [
      {
        title: 'Direction',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Comprendre les causes',
          'Évaluer l\'impact business',
          'Valider les améliorations',
          'Allouer les ressources'
        ],
        niveau: 'Niveau 1-2 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Shield,
        color: 'green',
        scenarios: [
          'Analyser la réponse',
          'Identifier les améliorations',
          'Implémenter les actions',
          'Suivre le plan d\'amélioration'
        ],
        niveau: 'Niveau 3-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer le Post-Incident Review?",
    heroDescription: "Le Post-Incident Review doit être livré <strong>après</strong> la résolution de l'incident pour analyser et améliorer.",
    moments: [
      {
        title: 'Après résolution incident',
        icon: CheckCircle2,
        color: 'green',
        description: 'Analyser l\'incident résolu pour amélioration'
      },
      {
        title: 'Incidents récurrents',
        icon: AlertTriangle,
        color: 'red',
        description: 'Comprendre les causes des récurrences'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur du Post-Incident Review",
    heroDescription: "Le Post-Incident Review transforme les incidents en <strong>opportunités d'amélioration</strong>, garantissant une amélioration continue.",
    avantages: [
      {
        title: 'Compréhension approfondie',
        desc: 'Analyse détaillée des causes et impacts',
        icon: Eye,
        color: 'blue'
      },
      {
        title: 'Amélioration continue',
        desc: 'Plan d\'amélioration structuré pour prévention',
        icon: TrendingUp,
        color: 'green'
      },
      {
        title: 'Réduction des récurrences',
        desc: 'Actions correctives pour éviter les répétitions',
        icon: Shield,
        color: 'orange'
      },
      {
        title: 'Apprentissage organisationnel',
        desc: 'Leçons apprises pour toute l\'organisation',
        icon: FileText,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI du Post-Incident Review',
      description: 'Le Post-Incident Review génère de la valeur en:',
      points: [
        'Réduisant les récurrences d\'incidents (économies de temps et coûts)',
        'Améliorant les processus (efficacité accrue)',
        'Renforçant la résilience (meilleure préparation)',
        'Optimisant les ressources (allocation efficace)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité du Post-Incident Review",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>rapport exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Résumé Exécutif',
        pages: '3-5 pages',
        contenu: 'Synthèse essentielle, causes principales, recommandations clés',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Rapport Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, analyses de base',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Rapport Complet',
        pages: '10-15 pages',
        contenu: 'Analyses approfondies, chronologie détaillée, plan d\'amélioration',
        pour: 'Équipe TI (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Rapport Géré',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, analyses approfondies, plan d\'amélioration structuré',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Rapport Optimisé',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, analyses complètes, plan d\'amélioration détaillé',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: incidents récurrents, processus non améliorés, manque d'apprentissage.
Besoins: Analyse approfondie, compréhension des causes, plan d'amélioration structuré.`);

// AFTER_ACTION_REPORT
export const AfterActionReportModal = createDocumentModal('AFTER_ACTION_REPORT', {
  esprit: {
    heroTitle: "L'Esprit de l'After-Action Report (AAR)",
    heroDescription: "L'After-Action Report est le <strong>rapport détaillé d'action après incident</strong> qui documente la chronologie, les actions prises, les résultats et les recommandations. C'est l'outil qui transforme la réponse en apprentissage.",
    pourquoi: {
      probleme: "Sans documentation détaillée de la réponse, les actions ne sont pas <strong>évaluées efficacement</strong>, les résultats ne sont pas mesurés, les recommandations ne sont pas suivies.",
      solution: "L'After-Action Report établit une <strong>documentation exhaustive</strong> pour évaluer la réponse, mesurer les résultats et identifier les améliorations."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si la réponse à un incident est une <strong>opération chirurgicale</strong>, l'After-Action Report est le <strong>rapport opératoire détaillé</strong> qui documente:",
      points: [
        "Chaque étape de l'opération (chronologie)",
        "Les actions prises (procédures)",
        "Les résultats obtenus (succès/échecs)",
        "Les recommandations pour améliorer (leçons apprises)"
      ],
      conclusion: "L'After-Action Report, c'est exactement ça: le <strong>rapport opératoire</strong> qui garantit que chaque réponse devient une opportunité d'amélioration."
    },
    piliers: [
      { icon: Clock, title: '1. Chronologie', desc: 'Chronologie détaillée de l\'incident', color: 'blue' },
      { icon: FileCheck, title: '2. Actions', desc: 'Actions prises et résultats obtenus', color: 'green' },
      { icon: Target, title: '3. Analyse', desc: 'Analyse de cause racine et impact', color: 'orange' },
      { icon: TrendingUp, title: '4. Recommandations', desc: 'Recommandations et plan de suivi', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Conforme NIST CSF/ITIL/ISO', desc: 'Aligné sur les meilleurs cadres de documentation' },
      { title: 'Documentation exhaustive', desc: 'Chronologie complète et actions détaillées' },
      { title: 'Évaluation de performance', desc: 'Mesure des résultats et objectifs' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au rapport exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer l'After-Action Report?",
    heroDescription: "L'After-Action Report s'adresse principalement à l'<strong>équipe d'intervention</strong> et aux <strong>gestionnaires d'incidents</strong> pour l'évaluation de la réponse.",
    profils: [
      {
        title: 'Équipe d\'Intervention',
        icon: Shield,
        color: 'red',
        scenarios: [
          'Documenter les actions',
          'Évaluer les résultats',
          'Identifier les améliorations',
          'Appliquer les recommandations'
        ],
        niveau: 'Niveau 3-5 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'blue',
        scenarios: [
          'Évaluer la performance',
          'Valider les recommandations',
          'Suivre le plan d\'action',
          'Améliorer les processus'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer l'After-Action Report?",
    heroDescription: "L'After-Action Report doit être livré <strong>après</strong> la résolution de l'incident pour évaluer la réponse.",
    moments: [
      {
        title: 'Après résolution incident',
        icon: CheckCircle2,
        color: 'green',
        description: 'Évaluer la réponse après résolution'
      },
      {
        title: 'Incidents majeurs',
        icon: AlertTriangle,
        color: 'red',
        description: 'Documenter en détail les incidents critiques'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur de l'After-Action Report",
    heroDescription: "L'After-Action Report transforme la réponse en <strong>apprentissage mesurable</strong>, garantissant une amélioration continue.",
    avantages: [
      {
        title: 'Documentation exhaustive',
        desc: 'Chronologie complète et actions détaillées',
        icon: FileText,
        color: 'blue'
      },
      {
        title: 'Évaluation de performance',
        desc: 'Mesure des résultats et objectifs',
        icon: Target,
        color: 'green'
      },
      {
        title: 'Recommandations actionnables',
        desc: 'Plan d\'action structuré pour amélioration',
        icon: TrendingUp,
        color: 'orange'
      },
      {
        title: 'Apprentissage mesurable',
        desc: 'Métriques pour suivre l\'amélioration',
        icon: BarChart,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI de l\'After-Action Report',
      description: 'L\'After-Action Report génère de la valeur en:',
      points: [
        'Améliorant la réponse future (efficacité accrue)',
        'Réduisant les délais de résolution (productivité)',
        'Optimisant les ressources (allocation efficace)',
        'Renforçant la préparation (meilleure réponse)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité de l'After-Action Report",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>rapport exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Résumé Exécutif',
        pages: '3-5 pages',
        contenu: 'Synthèse essentielle, actions principales, recommandations clés',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Rapport Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, chronologie de base',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Rapport Complet',
        pages: '10-15 pages',
        contenu: 'Chronologie détaillée, analyses approfondies, plan d\'action',
        pour: 'Équipe d\'intervention (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Rapport Géré',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, analyses approfondies, plan d\'action structuré',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Rapport Optimisé',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, analyses complètes, plan d\'action détaillé',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents de sécurité fréquents.
Enjeux TI: réponse non évaluée, résultats non mesurés, améliorations non suivies.
Besoins: Documentation exhaustive, évaluation de performance, plan d'action structuré.`);

// LESSONS_LEARNED_REPORT
export const RapportLeconsApprisesModal = createDocumentModal('LESSONS_LEARNED_REPORT', {
  esprit: {
    heroTitle: "L'Esprit du Rapport de Leçons Apprises",
    heroDescription: "Le Rapport de Leçons Apprises est le <strong>document synthétique</strong> qui documente les bonnes pratiques identifiées, les améliorations à apporter et les actions correctives suite à un incident. C'est l'outil qui transforme l'expérience en savoir organisationnel.",
    pourquoi: {
      probleme: "Sans synthèse des leçons apprises, les bonnes pratiques ne sont pas <strong>partagées</strong>, les améliorations ne sont pas identifiées, les erreurs se répètent.",
      solution: "Le Rapport de Leçons Apprises établit une <strong>synthèse structurée</strong> pour partager les bonnes pratiques, identifier les améliorations et mettre en place les actions correctives."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident est une <strong>leçon de vie</strong>, le Rapport de Leçons Apprises est le <strong>carnet de notes</strong> qui documente:",
      points: [
        "Ce qui a bien fonctionné (bonnes pratiques)",
        "Ce qui doit être amélioré (points d'amélioration)",
        "Les leçons à retenir (insights clés)",
        "Les actions à prendre (plan d'action)"
      ],
      conclusion: "Le Rapport de Leçons Apprises, c'est exactement ça: le <strong>carnet de notes</strong> qui garantit que chaque incident devient une source de savoir organisationnel."
    },
    piliers: [
      { icon: CheckCircle2, title: '1. Succès', desc: 'Ce qui a bien fonctionné', color: 'green' },
      { icon: AlertTriangle, title: '2. Amélioration', desc: 'Ce qui doit être amélioré', color: 'orange' },
      { icon: TrendingUp, title: '3. Leçons', desc: 'Leçons apprises', color: 'blue' },
      { icon: FileCheck, title: '4. Actions', desc: 'Actions correctives et suivi', color: 'purple' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO', desc: 'Aligné sur les meilleurs cadres d\'amélioration continue' },
      { title: 'Synthèse claire', desc: 'Document synthétique et actionnable' },
      { title: 'Partage de connaissances', desc: 'Bonnes pratiques partagées organisationnellement' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au rapport exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Rapport de Leçons Apprises?",
    heroDescription: "Le Rapport de Leçons Apprises s'adresse principalement à <strong>toute l'organisation</strong> pour le partage de connaissances.",
    profils: [
      {
        title: 'Toute l\'Organisation',
        icon: UsersIcon,
        color: 'blue',
        scenarios: [
          'Apprendre des incidents',
          'Partager les bonnes pratiques',
          'Éviter les erreurs répétées',
          'Améliorer les processus'
        ],
        niveau: 'Niveau 1-3 recommandé'
      },
      {
        title: 'Gestionnaires d\'Incidents',
        icon: Building2,
        color: 'green',
        scenarios: [
          'Implémenter les améliorations',
          'Suivre les actions correctives',
          'Partager les leçons',
          'Améliorer les processus'
        ],
        niveau: 'Niveau 3-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer le Rapport de Leçons Apprises?",
    heroDescription: "Le Rapport de Leçons Apprises doit être livré <strong>après</strong> le Post-Incident Review pour synthétiser et partager.",
    moments: [
      {
        title: 'Après analyse post-incident',
        icon: CheckCircle2,
        color: 'green',
        description: 'Synthétiser les leçons après analyse'
      },
      {
        title: 'Partage organisationnel',
        icon: UsersIcon,
        color: 'blue',
        description: 'Partager les connaissances avec l\'organisation'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur du Rapport de Leçons Apprises",
    heroDescription: "Le Rapport de Leçons Apprises transforme l'expérience en <strong>savoir partagé</strong>, garantissant une amélioration continue.",
    avantages: [
      {
        title: 'Partage de connaissances',
        desc: 'Bonnes pratiques partagées organisationnellement',
        icon: UsersIcon,
        color: 'blue'
      },
      {
        title: 'Synthèse claire',
        desc: 'Document synthétique et actionnable',
        icon: FileText,
        color: 'green'
      },
      {
        title: 'Prévention des erreurs',
        desc: 'Actions correctives pour éviter les répétitions',
        icon: Shield,
        color: 'orange'
      },
      {
        title: 'Amélioration continue',
        desc: 'Plan d\'action structuré pour amélioration',
        icon: TrendingUp,
        color: 'purple'
      },
    ],
    roi: {
      title: 'ROI du Rapport de Leçons Apprises',
      description: 'Le Rapport de Leçons Apprises génère de la valeur en:',
      points: [
        'Réduisant les erreurs répétées (économies de temps et coûts)',
        'Partageant les bonnes pratiques (efficacité accrue)',
        'Améliorant les processus (meilleure performance)',
        'Renforçant la culture d\'apprentissage (résilience organisationnelle)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité du Rapport de Leçons Apprises",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>rapport exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Résumé Exécutif',
        pages: '3-5 pages',
        contenu: 'Synthèse essentielle, leçons clés, actions prioritaires',
        pour: 'Toute l\'organisation (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Rapport Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, synthèse complète',
        pour: 'Gestionnaires (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Rapport Complet',
        pages: '10-15 pages',
        contenu: 'Analyses approfondies, plan d\'action détaillé',
        pour: 'Équipe TI (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Rapport Géré',
        pages: '15-20 pages',
        contenu: 'Métriques, KPIs, analyses approfondies, plan d\'action structuré',
        pour: 'Conformité / Audit (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Rapport Optimisé',
        pages: '20-25 pages',
        contenu: 'Documentation exhaustive, analyses complètes, plan d\'action détaillé',
        pour: 'Certification / Excellence (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production.
Maturité TI: équipe TI très limitée, incidents fréquents.
Enjeux TI: erreurs répétées, bonnes pratiques non partagées, améliorations non identifiées.
Besoins: Synthèse claire, partage de connaissances, plan d'action structuré.`);

// RAPPORT_INCIDENT
export const RapportIncidentModal = createDocumentModal('RAPPORT_INCIDENT', {
  esprit: {
    heroTitle: "L'Esprit du Rapport d'Incident TI",
    heroDescription: "Le Rapport d'Incident est le <strong>document de traçabilité</strong> qui documente complètement un incident TI, de sa détection à sa résolution. C'est l'outil qui transforme les incidents en apprentissage organisationnel.",
    pourquoi: {
      probleme: "Sans rapport d'incident structuré, les incidents ne sont pas <strong>documentés</strong>, les leçons ne sont pas apprises, les récurrences augmentent, la conformité est compromise.",
      solution: "Le Rapport d'Incident documente <strong>complètement</strong> chaque incident, de la chronologie à l'analyse de cause racine, garantissant la traçabilité et l'amélioration continue."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si un incident TI est un <strong>accident de la route</strong>, le Rapport d'Incident est le <strong>rapport de police</strong> qui documente:",
      points: [
        "Quand et comment l'incident s'est produit (chronologie)",
        "Qui était impliqué (personnes affectées)",
        "Quels dommages ont été causés (impacts)",
        "Comment l'incident a été résolu (actions prises)",
        "Comment prévenir les récurrences (recommandations)"
      ],
      conclusion: "Le Rapport d'Incident, c'est exactement ça: le <strong>document officiel</strong> qui garantit que chaque incident est documenté, analysé et utilisé pour améliorer la sécurité."
    },
    piliers: [
      { icon: AlertCircle, title: '1. Identification & Classification', desc: 'Détails de base et classification ITIL', color: 'red' },
      { icon: Clock, title: '2. Chronologie & Détection', desc: 'Timeline complète de l\'incident', color: 'orange' },
      { icon: AlertTriangle, title: '3. Analyse & Impact', desc: 'Cause racine et impacts business', color: 'yellow' },
      { icon: Shield, title: '4. Réponse & Résolution', desc: 'Actions de confinement, éradication, restauration', color: 'green' },
      { icon: FileCheck, title: '5. Conformité & Documentation', desc: 'Loi 25, leçons apprises, recommandations', color: 'blue' },
    ],
    prismeAdvantages: [
      { title: 'Conforme ITIL/COBIT/ISO/NIST', desc: 'Aligné sur les meilleurs cadres de gestion d\'incidents' },
      { title: 'Documentation complète', desc: 'Chronologie, analyse, impacts, réponse, conformité' },
      { title: 'Orienté amélioration', desc: 'Recommandations et leçons apprises pour prévenir les récurrences' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) au rapport exhaustif (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer le Rapport d'Incident?",
    heroDescription: "Le Rapport s'adresse principalement à la <strong>direction</strong>, à la <strong>gouvernance</strong>, aux <strong>assureurs</strong> et aux <strong>autorités de conformité</strong> qui doivent comprendre et apprendre des incidents.",
    profils: [
      {
        title: 'Direction / Gouvernance',
        icon: Building2,
        color: 'red',
        scenarios: [
          'Comprendre l\'impact business',
          'Valider les mesures prises',
          'Approuver les recommandations',
          'Décider des investissements'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Équipe TI / Sécurité',
        icon: Shield,
        color: 'orange',
        scenarios: [
          'Documenter les incidents',
          'Analyser les causes',
          'Implémenter les recommandations',
          'Améliorer les processus'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
      {
        title: 'Assureurs / Conformité',
        icon: FileCheck,
        color: 'blue',
        scenarios: [
          'Démontrer la gestion d\'incidents',
          'Répondre aux obligations légales',
          'Soutenir les réclamations',
          'Prouver la conformité'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
  },
  quand: {
    heroTitle: "Quand livrer le Rapport d'Incident?",
    heroDescription: "Le Rapport doit être livré <strong>après chaque incident significatif</strong>, idéalement dans les <strong>48-72 heures</strong> suivant la résolution pour garantir la fraîcheur des informations.",
    moments: [
      {
        title: 'Après résolution d\'un incident',
        icon: CheckCircle2,
        color: 'green',
        description: 'Documenter complètement l\'incident résolu pour traçabilité et apprentissage'
      },
      {
        title: 'Pour conformité légale',
        icon: FileCheck,
        color: 'blue',
        description: 'Répondre aux obligations Loi 25, CAI, ou autres exigences réglementaires'
      },
      {
        title: 'Pour réclamation d\'assurance',
        icon: DollarSign,
        color: 'yellow',
        description: 'Soutenir les réclamations d\'assurance avec documentation complète'
      },
      {
        title: 'Pour amélioration continue',
        icon: TrendingUp,
        color: 'purple',
        description: 'Utiliser les leçons apprises pour améliorer les processus et prévenir les récurrences'
      },
    ],
  },
  valeur: {
    heroTitle: "La Valeur du Rapport d'Incident",
    heroDescription: "Le Rapport transforme les incidents en <strong>opportunités d'apprentissage</strong> et de <strong>renforcement de la sécurité</strong>, garantissant la conformité et l'amélioration continue.",
    avantages: [
      {
        title: 'Traçabilité complète',
        desc: 'Documentation exhaustive pour audit et conformité',
        icon: FileText,
        color: 'blue'
      },
      {
        title: 'Amélioration continue',
        desc: 'Leçons apprises pour prévenir les récurrences',
        icon: TrendingUp,
        color: 'green'
      },
      {
        title: 'Conformité légale',
        desc: 'Répond aux obligations Loi 25, CAI, et autres exigences',
        icon: FileCheck,
        color: 'purple'
      },
      {
        title: 'Support d\'assurance',
        desc: 'Documentation complète pour réclamations',
        icon: DollarSign,
        color: 'yellow'
      },
    ],
    roi: {
      title: 'ROI du Rapport d\'Incident',
      description: 'Le Rapport d\'Incident génère de la valeur en:',
      points: [
        'Réduisant les récurrences d\'incidents similaires (économies de temps et coûts)',
        'Démontrant la conformité (évite les sanctions et amendes)',
        'Soutenant les réclamations d\'assurance (compensation financière)',
        'Améliorant la réputation (confiance des clients et partenaires)'
      ]
    },
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité du Rapport d'Incident",
    heroDescription: "Du <strong>résumé exécutif</strong> au <strong>rapport exhaustif</strong>, chaque niveau répond à des besoins différents.",
    comparaison: [
      {
        niveau: 1,
        titre: 'Résumé Exécutif',
        pages: '3-5 pages',
        contenu: 'Identification, chronologie essentielle, résolution, recommandations clés',
        pour: 'Direction (vue d\'ensemble rapide)',
        couleur: 'gray'
      },
      {
        niveau: 2,
        titre: 'Rapport Structuré',
        pages: '6-10 pages',
        contenu: 'Toutes les sections principales, analyse de cause racine, impacts business',
        pour: 'Gouvernance (documentation standard)',
        couleur: 'blue'
      },
      {
        niveau: 3,
        titre: 'Rapport Complet',
        pages: '10-15 pages',
        contenu: 'Analyse approfondie, chronologie détaillée, plan d\'amélioration',
        pour: 'Équipe TI (documentation complète)',
        couleur: 'green'
      },
      {
        niveau: 4,
        titre: 'Rapport Géré',
        pages: '15-20 pages',
        contenu: 'Conformité Loi 25, métriques (MTTR), analyse comparative, leçons apprises',
        pour: 'Conformité / Assureurs (documentation exhaustive)',
        couleur: 'purple'
      },
      {
        niveau: 5,
        titre: 'Rapport Optimisé',
        pages: '20-25 pages',
        contenu: 'Analyse forensique approfondie, KPIs détaillés, plan d\'amélioration continue structuré',
        pour: 'Audit / Certification (documentation maximale)',
        couleur: 'orange'
      },
    ],
  },
}, `Client: PME manufacturière de 80 employés.
Incident: Attaque par rançongiciel sur serveur de fichiers SRV-FILES-01.
Détection: 15 mars 2024 à 08h30 par l'équipe TI.
Résolution: 15 mars 2024 à 14h45 après restauration complète depuis sauvegarde.
Impact: Interruption de service de 6h15, 50 utilisateurs affectés, données sensibles cryptées.
Actions: Confinement immédiat, isolation réseau, restauration depuis sauvegarde, vérification complète.
Recommandations: Amélioration des sauvegardes, formation utilisateurs, renforcement de la sécurité.`);

// POLITIQUE_CYBER
export const PolitiqueCybersecuriteModal = createDocumentModal('POLITIQUE_CYBER', {
  esprit: {
    heroTitle: "L'Esprit de la Politique de Cybersécurité sur l'Utilisation des Actifs TI",
    heroDescription: "La Politique de Cybersécurité est le <strong>document de gouvernance</strong> qui définit les règles et procédures pour l'utilisation sécurisée des actifs TI. C'est l'outil qui transforme les risques en protection structurée.",
    pourquoi: {
      probleme: "Sans politique de cybersécurité claire, les utilisateurs ne savent pas <strong>comment utiliser</strong> les actifs TI de manière sécurisée, les risques augmentent, la conformité est compromise.",
      solution: "La Politique de Cybersécurité établit des <strong>règles claires</strong> pour l'utilisation des actifs TI, couvrant tous les aspects de la sécurité, garantissant la protection et la conformité."
    },
    analogie: {
      title: "Une Analogie Simple",
      content: "Si les actifs TI sont une <strong>maison</strong>, la Politique de Cybersécurité est le <strong>règlement de copropriété</strong> qui définit:",
      points: [
        "Comment utiliser les espaces communs (réseaux, serveurs)",
        "Quelles sont les règles de sécurité (mots de passe, authentification)",
        "Comment gérer les accès (contrôles d'accès)",
        "Quelles sont les sanctions en cas de violation"
      ],
      conclusion: "La Politique de Cybersécurité, c'est exactement ça: le <strong>règlement</strong> qui garantit que tous les utilisateurs savent comment utiliser les actifs TI de manière sécurisée."
    },
    piliers: [
      { icon: Shield, title: '1. Utilisations Autorisées', desc: 'Définit ce qui est permis et interdit', color: 'purple' },
      { icon: UsersIcon, title: '2. Contrôles d\'Accès', desc: 'Gestion des accès et authentification', color: 'indigo' },
      { icon: AlertTriangle, title: '3. Gestion des Violations', desc: 'Procédures pour gérer les incidents', color: 'red' },
      { icon: CheckCircle2, title: '4. Conformité & Sanctions', desc: 'Règles de conformité et sanctions', color: 'emerald' },
      { icon: Layers, title: '5. Formation & Sensibilisation', desc: 'Plan de formation et sensibilisation', color: 'orange' },
    ],
    prismeAdvantages: [
      { title: 'Alignée sur NIST/ITIL/COBIT/ISO', desc: 'Conforme aux meilleurs cadres de cybersécurité' },
      { title: 'Couvre tous les aspects', desc: 'Utilisations, accès, authentification, violations, conformité' },
      { title: 'Adaptée au contexte', desc: 'Personnalisée selon les besoins et groupes d\'utilisateurs' },
      { title: '5 niveaux de détail', desc: 'Du résumé exécutif (Niveau 1) à la politique complète (Niveau 5)' },
    ],
  },
  qui: {
    heroTitle: "À qui livrer la Politique de Cybersécurité?",
    heroDescription: "La Politique s'adresse principalement à <strong>tous les utilisateurs</strong> des actifs TI, à la <strong>direction</strong> et à la <strong>gouvernance</strong> qui doivent comprendre et appliquer les règles de sécurité.",
    profils: [
      {
        title: 'Tous les Utilisateurs',
        icon: UsersIcon,
        color: 'purple',
        scenarios: [
          'Comprendre les règles de sécurité',
          'Savoir comment utiliser les actifs TI',
          'Connaître les procédures',
          'Respecter la conformité'
        ],
        niveau: 'Niveau 2-3 recommandé'
      },
      {
        title: 'Direction / RH',
        icon: Star,
        color: 'indigo',
        scenarios: [
          'Valider la politique',
          'Communiquer aux employés',
          'Gérer les violations',
          'Assurer la conformité'
        ],
        niveau: 'Niveau 3-4 recommandé'
      },
      {
        title: 'Équipe TI / Sécurité',
        icon: Shield,
        color: 'emerald',
        scenarios: [
          'Implémenter la politique',
          'Gérer les accès',
          'Surveiller les violations',
          'Former les utilisateurs'
        ],
        niveau: 'Niveau 4-5 recommandé'
      },
    ],
    personas: [
      { role: 'Utilisateur', focus: 'Règles, procédures, conformité', icon: UsersIcon },
      { role: 'Direction', focus: 'Validation, communication, conformité', icon: Star },
      { role: 'Équipe TI', focus: 'Implémentation, gestion, surveillance', icon: Shield },
    ],
  },
  quand: {
    heroTitle: "Quand et pourquoi le client en a besoin?",
    heroDescription: "La Politique est nécessaire lorsque le client doit <strong>structurer la sécurité</strong> des actifs TI et <strong>assurer la conformité</strong> réglementaire.",
    questions: [
      {
        question: 'Avez-vous des utilisateurs qui ne savent pas comment utiliser les actifs TI de manière sécurisée?',
        siNon: '→ La Politique définit clairement les règles et procédures'
      },
      {
        question: 'Vos risques de cybersécurité augmentent-ils?',
        siNon: '→ La Politique réduit les risques en établissant des règles claires'
      },
      {
        question: 'Avez-vous besoin de conformité (Loi 25, ISO 27001)?',
        siNon: '→ La Politique est un élément clé de la conformité'
      },
      {
        question: 'Y a-t-il des violations de sécurité fréquentes?',
        siNon: '→ La Politique établit des procédures pour gérer les violations'
      },
      {
        question: 'Votre direction demande-t-elle une politique de sécurité?',
        siNon: '→ La Politique démontre une gestion professionnelle de la sécurité'
      },
    ],
    signaux: [
      'Utilisateurs non formés à la sécurité',
      'Risques de cybersécurité élevés',
      'Besoin de conformité réglementaire',
      'Violations de sécurité fréquentes',
      'Direction demande une politique',
      'Manque de règles claires'
    ],
    timing: [
      { timing: 'Lors d\'une évaluation de sécurité', raison: 'Pour structurer la sécurité des actifs TI' },
      { timing: 'Lors d\'une conformité', raison: 'Pour démontrer la conformité réglementaire' },
      { timing: 'Après un incident de sécurité', raison: 'Pour prévenir les récidives' },
      { timing: 'Lors d\'une nouvelle gouvernance', raison: 'Pour établir les règles dès le départ' },
    ],
  },
  valeur: {
    heroTitle: "Comment expliquer la valeur au client?",
    heroDescription: "La Politique de Cybersécurité n'est pas juste un document, c'est un <strong>outil de protection</strong> qui transforme les risques en sécurité structurée.",
    benefices: [
      {
        benefice: 'Règles claires',
        detail: 'Définit précisément comment utiliser les actifs TI de manière sécurisée',
        icon: Shield,
        color: 'purple'
      },
      {
        benefice: 'Réduction des risques',
        detail: 'Réduit les risques de cybersécurité en établissant des règles claires',
        icon: AlertTriangle,
        color: 'red'
      },
      {
        benefice: 'Conformité réglementaire',
        detail: 'Démontre la conformité avec Loi 25, ISO 27001, NIST CSF',
        icon: CheckCircle2,
        color: 'emerald'
      },
      {
        benefice: 'Gestion des violations',
        detail: 'Établit des procédures claires pour gérer les violations de sécurité',
        icon: Target,
        color: 'indigo'
      },
      {
        benefice: 'Formation structurée',
        detail: 'Plan de formation et sensibilisation pour tous les utilisateurs',
        icon: UsersIcon,
        color: 'blue'
      },
      {
        benefice: 'Protection des actifs',
        detail: 'Protège les actifs TI en établissant des règles de sécurité',
        icon: Shield,
        color: 'orange'
      }
    ],
    roi: {
      title: 'Retour sur investissement (ROI)',
      content: 'Une Politique de Cybersécurité bien structurée peut générer un ROI de 500-1000% en:',
      points: [
        { label: 'Prévention des incidents', value: 'Éviter un incident majeur = économie de 100-500K$+' },
        { label: 'Conformité réglementaire', value: 'Éviter les amendes = économie de 25-100K$/an' },
        { label: 'Réduction des risques', value: 'Gestion proactive = réduction des risques de 50-80%' },
        { label: 'Formation efficace', value: 'Utilisateurs formés = réduction des erreurs de 40-60%' }
      ],
      cout: 'Coût d\'une Politique de Cybersécurité PRIsME: 4-12K$ (selon niveau)',
      valeur: 'Valeur générée: 100-500K$+ en économies et protection'
    },
    arguments: [
      {
        persona: 'Pour la Direction',
        arguments: [
          'Protection des actifs TI',
          'Conformité réglementaire',
          'Réduction des risques',
          'Gestion professionnelle de la sécurité'
        ]
      },
      {
        persona: 'Pour les Utilisateurs',
        arguments: [
          'Règles claires et accessibles',
          'Formation et sensibilisation',
          'Protection des données',
          'Conformité facilitée'
        ]
      },
      {
        persona: 'Pour l\'Équipe TI',
        arguments: [
          'Outil de gestion de la sécurité',
          'Procédures claires',
          'Gestion des violations',
          'Formation structurée'
        ]
      }
    ],
  },
  niveaux: {
    heroTitle: "Les 5 Niveaux de Qualité",
    heroDescription: "PRIsME offre <strong>5 niveaux</strong> de Politique de Cybersécurité, du résumé exécutif à la politique complète avec tous les détails. Chaque niveau s'adapte à un besoin spécifique.",
    comparisonTable: [
      { critere: 'Pages (approximatif)', values: ['5-8', '12-20', '25-40', '45-65', '70-100'] },
      { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + Utilisateurs', 'Tous utilisateurs', 'Gouvernance complète', 'Conformité complète'] },
      { critere: 'Sections couvertes', values: ['5-8', '10-15', '15-20', '20-25', '25-30'] },
      { critere: 'Détail par aspect', values: ['Mentionné', 'Basique', 'Détaillé', 'Très détaillé', 'Complet avec exemples'] },
      { critere: 'Procédures', values: ['Mentionnées', 'Basiques', 'Détaillées', 'Très détaillées', 'Complètes avec scénarios'] },
      { critere: 'Plan de formation', values: ['Mentionné', 'Basique', 'Détaillé', 'Très détaillé', 'Complet avec calendrier'] },
      { critere: 'Prix indicatif', values: ['4-6K$', '6-9K$', '9-15K$', '15-22K$', '22-30K$'] },
    ],
    niveauDetails: [
      {
        inclus: [
          'Résumé exécutif (2-3 pages)',
          '5-8 sections principales',
          'Règles basiques',
          'Procédures mentionnées',
          'Langage simple, accessible'
        ],
        quandUtiliser: 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, sécurité simple.'
      },
      {
        inclus: [
          'Résumé exécutif + sections standardisées',
          '10-15 sections détaillées',
          'Règles détaillées',
          'Procédures basiques',
          'Plan de formation basique'
        ],
        quandUtiliser: 'Pour PME standard, besoin de structuration, première approche formelle de la sécurité.'
      },
      {
        inclus: [
          'Tout le niveau 2, PLUS:',
          '15-20 sections très détaillées',
          'Règles très détaillées',
          'Procédures détaillées',
          'Plan de formation détaillé',
          'Intégration NIST/ITIL/COBIT/ISO'
        ],
        quandUtiliser: 'Pour PME en croissance, besoin complet, alimenter gouvernance, équipe TI formelle.'
      },
      {
        inclus: [
          'Tout le niveau 3, PLUS:',
          '20-25 sections exhaustives',
          'Règles exhaustives avec exemples',
          'Procédures très détaillées',
          'Plan de formation très détaillé',
          'Gestion des violations complète',
          'Ton très professionnel, conformité-ready'
        ],
        quandUtiliser: 'Pour entreprises matures, gouvernance importante, conformité requise, comité TI formel.'
      },
      {
        inclus: [
          'Tout le niveau 4, PLUS:',
          '25-30 sections complètes',
          'Règles complètes avec scénarios',
          'Procédures complètes avec exemples',
          'Plan de formation complet avec calendrier',
          'Gestion des violations exhaustive',
          'Document conformité-ready',
          'Ton exécutif, très visuel'
        ],
        quandUtiliser: 'Pour grandes entreprises, conformité complète, gouvernance exécutive, présentation CA.'
      },
    ],
  },
}, `Client: Cabinet d'avocats de 50 employés, 2 bureaux au Québec.
Objectifs d'affaires: protéger les données clients, assurer la conformité Loi 25, améliorer la sécurité.
Maturité TI: équipe TI limitée, dépendance à des fournisseurs externes, parc hétérogène.
Enjeux sécurité: Accès non contrôlés, mots de passe faibles, pas de politique claire, risques de violation de données clients.
Conformité: Besoin de conformité Loi 25, gestion des données personnelles requise.`);

// Export des modals
export { default as Roadmap24Modal } from './Roadmap24Modal';

